/*!
// <copyright file="AppletInteraction.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util, appletMessaging, messaging, $format } from "@ax/office-shared-lib";
import { JQueryThrottlingHandler } from '@ax/office-shared-lib/dist/Throttling';
import { telemetry } from "@ax/office-cdn-app-telemetry";
import { resources } from "@ax/office-cdn-app-resources";
import { officeInteraction } from "./OfficeInteraction";
import { appState } from "./AppState";
import { appElements } from "./Elements";
import { dialogs } from "./Dialogs";
import { notificationHandling } from "./NotificationHandling";
import { operationHandling } from "./OperationHandling";
import { telemetryLogging } from "./TelemetryLogging";
import { authentication } from "./Authentication";
import { IApplication } from "./IApplication";
import { AuthError } from "@azure/msal-browser";
import * as $ from "jquery";
import * as Globalize from "globalize";

/** Encapsulates the logic necessary for communicating with applets. */
export namespace appletInteraction {

    interface RetrievedAppletRegistration extends appState.CommonAppletRegistration, appState.RelativeAppletRegistration {

        /** The localized name of the applet. */
        LocalizedName?: string;

        /** The qualified path to the applet. */
        Path?: string;
    }

    interface RetrievedResourceRegistration extends appState.CommonResourceRegistration, appState.RelativeResourceRegistration { }

    export let app: IApplication;

    /** An array of applet registration data (JSON records retrieved from the registration entity). */
    export let registeredApplets: RetrievedAppletRegistration[];

    /** Maps an applet's ID to its callback to perform once the applet becomes ready. */
    export let pendingAppletReadyCallbacks: util.Map;

    /** Maps an applet's ID to its loaded frame. */
    export let appletMap: util.Map<AppletInformation>;

    /** Maps a registered resource ID to the resource's URL. */
    export let registeredResources: util.Map<string>;

    /** Maps a resource's ID to its callbacks to perform once the resource becomes ready. */
    export let pendingResourceReadyCallbacks: util.Map;

    /** Maps a resource's ID to the loaded frame. */
    export let resourceMap: util.Map<ResourceInformation>;

    /** Handles messaging to and from applets. */
    export let messageHandler: appletMessaging.MessageHandler;

    /** The stack of ready applets, where the applet at the front (index 0) of the array is active. */
    export let appletStack: appletInteraction.AppletInformation[];

    /** The format used to form resource URLs, where the first param is the host, the second is the resource's URL, the third is the resource's ID, and the fourth is the current language. */
    export const _ResourceUrlFormat = "{0}?ResourceID={1}&Language={2}";

    /** Represents information about a resource, where a resource has an identifier and a URL. */
    export class WellknownResourceInformation {

        /** The ID of the resource. */
        public ID: string;

        /** The URL for the resource. */
        public Url: string;

        /**
         * @param id The ID of the resource.
         * @param url The URL for the resource.
         */
        constructor(id: string, url: string) {
            this.ID = id;
            this.Url = url;
        }
    }

    /** Represents resources that are well known to the app and must always be loaded. */
    export const WellKnownResources = {

        /** The resource used to access data. */
        Data: new appletInteraction.WellknownResourceInformation(appletMessaging.WellKnownResources.Data, appState.appsForOfficeNode + '/ODataInteraction.html'),

        /** The resource used to authenticate. */
        Auth: new appletInteraction.WellknownResourceInformation("authentication-resource", appState.appsForOfficeNode + '/Public/CheckAuthentication.html'),

        /** The resource used to fetch the server configuration. */
        Config: new appletInteraction.WellknownResourceInformation("config-resource", appState.appsForOfficeNode + '/Public/Config.html'),

        /** The resource used to fetch the enhanced server configuration. */
        EnhancedConfig: new appletInteraction.WellknownResourceInformation("enhanced-config-resource", appState.appsForOfficeNode + '/EnhancedConfig.html')
    };

    /** The max number of retries that should be performed when an initial request is sent to a frame. */
    export const InitialRequestMaxRetries = 10;

    /** The amount of time to wait for an applet to respond to an update applet options request. */
    export const UpdateAppletOptionsTimeout = 5000;

    /** The name of the applet registration entity. */
    export const AppletRegistrationEntityName = "OfficeAppAppletRegistration";

    /** The public collection name of the applet registration entity. */
    export const AppletRegistrationEntityCollectionName = "OfficeAppAppletRegistrations";

    /** The name of the resource registration entity. */
    export const ResourceRegistrationEntityName = "OfficeAppResourceRegistration";

    /** The public collection name of the resource registration entity. */
    export const ResourceRegistrationEntityCollectionName = "OfficeAppResourceRegistrations";

    /** The regular expression used to validate relative URLs. */
    export const RelativeUrlValidationRegex = /^(\/.+)+(\.[a-zA-Z]{2,4})$/g;

    /** The regular expression used to determine if a label is actually a label ID. */
    export const LabelIdValidationRegex = /^@.+/;

    /** Contains the identifier formats for applet related HTML elements generated. */
    export const _IdentifierFormats = {

        /** The format used to create the ID for an applet frame. The format takes a single parameter representing the ID of the applet. */
        AppletFrame: "appletFrame_{0}",

        /** The format used to create the ID for a resource frame. The format takes a single parameter representing the ID of the resource. */
        ResourceFrame: "resourceFrame_{0}",

        /** The format used to create the ID for an applet options element. The format takes a single parameter representing the ID of the applet. */
        AppletOptionsElement: "appletOptions_{0}",

        /**
         * The format used to create the ID for an applet option control. The format takes three parameters; the first parameter is ID of the applet,
         * the second is the index of the options group the option is contained within, the third is the index of the option within its group.
         */
        AppletOptionControl: "option_{0}_{1}_{2}",

        /** The format used to create the ID for an applet selection item element. The format takes a single parameter representing the ID of the applet. */
        AppletSelectionItemElement: "appletSelectionItem_{0}"
    };

    /** The unique ID to use that represents the app, for resource messaging purposes. */
    const SendAppResourceRequestAppID = "DynamicsApp";

    /** The max number of applet requests that should be processed at a time. */
    export const MaxAppletRequestCount = 5;

    /** The number of applet requests currently being processed. */
    export let activeAppletRequestCount: number;

    /** The applet requests that have been throttled and are currently pending processing. */
    export let pendingAppletRequestEvents: util.AddinMessageEvent<appletMessaging.Requests.Request<any>>[];

    /** The telemetry log handler for applet request throttling. */
    export let throttledAppletRequestLogger: ThrottledAppletRequestLogger;

    /** Initializes the data structures and message handling necessary for handling applet office requests. */
    export function initialize(): void {

        // Initialize empty maps
        appletInteraction.pendingAppletReadyCallbacks = new util.Map();
        appletInteraction.appletMap = new util.Map();
        appletInteraction.pendingResourceReadyCallbacks = new util.Map();
        appletInteraction.resourceMap = new util.Map();
        appletInteraction.appletStack = [];

        // Initialize throttling properties
        appletInteraction.activeAppletRequestCount = 0;
        appletInteraction.pendingAppletRequestEvents = [];
        appletInteraction.throttledAppletRequestLogger = new appletInteraction.ThrottledAppletRequestLogger();

        appletInteraction.resetRegistrations();

        // Create message handler and add listeners for applet messages
        appletInteraction.messageHandler = new appletMessaging.MessageHandler(resources.appletMessaging.requestTimedOutMessage.bind(resources.appletMessaging), appletInteraction.verifyMessage);
        window.addEventListener("message", appletInteraction.messageHandler.listen, false);
        appletInteraction.messageHandler.registerMessageHandler(messaging.MessageTypes.resourceReadyRequest, appletInteraction._handleResourceReadyRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.AppletReady, appletInteraction._handleAppletReadyRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.UpdateAppletOptions, appletInteraction.handleUpdateAppletOptionsRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.SaveSettings, appletInteraction.handleSaveSettingsRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.SetSessionStorageItem, appletInteraction.handleSetSessionStorageItemRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.AddNotification, appletInteraction.handleAddNotificationRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.PromptUser, appletInteraction.handlePromptUserRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.LogEvent, appletInteraction.handleLogEventRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.BeginOperation, appletInteraction.handleBeginOperationRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.UpdateOperation, appletInteraction.handleUpdateOperationRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.EndOperation, appletInteraction.handleEndOperationRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.SetStatus, appletInteraction.handleSetStatusRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.OpenOptions, appletInteraction.handleOpenOptionsRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.CloseOptions, appletInteraction.handleCloseOptionsRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.ChangeConnectionAddress, handleChangeConnectionAddressRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.Resource, appletInteraction.handleResourceRequest);

        // Initialize Office interaction
        officeInteraction.initialize();
    }

    /** Executed whenever an applet request is considered to be completed, decrementing appropriate state and executing the next pending request appropriately. */
    export function appletRequestCompleted(): void {

        // Decremenet the active applet request count. If that drops the count below the max count (which it should unless something unexpected allowed
        // an extra request to be processed) and there is at least one pending event, process the next pending event.
        if (--appletInteraction.activeAppletRequestCount < appletInteraction.MaxAppletRequestCount && appletInteraction.pendingAppletRequestEvents.length > 0) {

            // Get the next pending event from the front of the queue
            const event = appletInteraction.pendingAppletRequestEvents.shift();

            // Execute the event's corresponding message handler (note that we bypass the listen method since the message was previously
            // verified and determined to be an applet request event)
            appletInteraction.messageHandler.getMessageHandler(event.data.Type)(event);
        }
    }

    /**
     * Registers an applet request handler with the message listener, incorporating appropriate logic to facilitate applet request throttling.
     * @param messageType The type of request for which to register the handler.
     * @param handler The handler to register.
     */
    export function registerAppletRequestHandler(messageType: string, handler: (event: util.AddinMessageEvent<appletMessaging.Requests.Request<any>>) => void): void {

        // Register a wrapped handler that incorporates appropriate applet request throttling logic
        appletInteraction.messageHandler.registerMessageHandler(
            messageType,
            (event: util.AddinMessageEvent<appletMessaging.Requests.Request<any>>): void => {

                // If the add-in is currently processing the max number of requests, increment the telemetry count and add the event
                // to the pending event queue to be processed once any active requests and proceeding events are processed. If the
                // event is added to the queue, the method returns early to avoid processing the event.
                if (appletInteraction.activeAppletRequestCount >= appletInteraction.MaxAppletRequestCount) {
                    appletInteraction.throttledAppletRequestLogger.incrementThrottledCount(messageType);
                    appletInteraction.pendingAppletRequestEvents.push(event);
                    return;
                }

                // Increment the active request count
                appletInteraction.activeAppletRequestCount++;

                // Execute the handler
                handler(event);

                // If the request doesn't have an expected response message, schedule the completion method (giving time for any async
                // calls already scheduled by the handler to complete)
                if (!(messageType in appletMessaging.expectedResponseTypes)) {
                    // tslint:disable-next-line no-string-based-set-timeout
                    window.setTimeout(appletInteraction.appletRequestCompleted);
                }
            });
    }

    /** Handles logging telemetry around applet request throttling. */
    export class ThrottledAppletRequestLogger {

        /** The current logger interval handle; 0 if there isn't a logger already scheduled. */
        private loggerIntervalHandle: number = 0;

        /** The property bag of current throttled request counts, keyed by message type. */
        private currentCounts: util.PropertyBag<number> = {};

        /** The similar to the current counts, this represents the last counts the scheduled logger encountered. */
        private lastCounts: util.PropertyBag<number>;

        /** The amount of time in milliseconds to wait between log intervals. */
        private static LogIntervalDelay = 2000;

        constructor() {
            this.log = this.log.bind(this);
        }

        /**
         * Incremenets the throttle count for the provided message type. If a logger hasn't yet been scheduled on an
         * interval, a logger is scheduled on a interval. That logger won't log telemetry until the counts haven't changed
         * between two intervals - this is to reduce the number of events written to telemetry.
         * @param messageType The type of message for which to incremenet the count.
         */
        public incrementThrottledCount(messageType: string): void {
            this.currentCounts[messageType] = (this.currentCounts[messageType] || 0) + 1;
            if (!this.loggerIntervalHandle) {
                this.lastCounts = $.extend({}, this.currentCounts);
                this.loggerIntervalHandle = window.setInterval(this.log, ThrottledAppletRequestLogger.LogIntervalDelay);
            }
        }

        /**
         * Handles logging the throttled request counts. If counts haven't changed between two intervals, then the telemetry
         * event is written, the interval is cleared, and state is reset; otherwise, if counts have changed between intervals,
         * the last counts are updated and the interval is allowed to continue.
         */
        private log(): void {
            if (this.lastCounts && this.didCountsChange()) {
                this.lastCounts = $.extend({}, this.currentCounts);
            } else {
                window.clearInterval(this.loggerIntervalHandle);
                telemetry.events.appletRequestsThrottled.log(JSON.stringify(this.currentCounts));
                this.loggerIntervalHandle = 0;
                this.currentCounts = {};
                delete this.lastCounts;
            }
        }

        /** Determines if the counts have changed since the last interval. */
        private didCountsChange(): boolean {
            let countsChanged = false;
            if (Object.keys(this.currentCounts).length !== Object.keys(this.lastCounts).length) {
                countsChanged = true;
            } else {
                for (const messageType in this.currentCounts) {
                    if (this.currentCounts[messageType] !== this.lastCounts[messageType]) {
                        countsChanged = true;
                        break;
                    }
                }
            }
            return countsChanged;
        }
    }

    /**
     * Determines whether the specified message is valid and should be handled.
     * @param event The message event.
     * @returns A value indicating whether the message should be handled.
     */
    export function verifyMessage(event: MessageEvent): boolean {
        var shouldHandleMessage = false;

        // The message event must have a message to be handled
        if (event.data !== undefined) {
            var message = event.data;

            // Retrieve the applet ID. Since we lose type information when posting messages, we can't just verify it's of a specific message
            // type; instead we know that request messages must contain the 'AppletID' property at their root level, and that response messages
            // must contain a 'Request' property that contains a request w/ the 'AppletID' property. As such we use those assumptions to try
            // to determine the applet ID. If a message doesn't meet either of this requirements, then it's not a supported applet request or
            // response message, and therefore has no applet ID.
            var appletID = message.AppletID;
            if (appletID === undefined && message.Request !== undefined) {
                appletID = message.Request.AppletID;
            }

            // If the message had an applet ID, only consider the message if the applet is registered;
            // otherwise determine if the message is a resource related message for a registered resource
            if (appletID !== undefined) {
                shouldHandleMessage = appletInteraction.appletMap.containsKey(appletID) || (message.Type == appletMessaging.MessageTypes.AppletReady && appletInteraction.pendingAppletReadyCallbacks.containsKey(appletID));
            } else {
                // Retrieve the resource ID. Since we lose type information when posting messages, we can't just verify it's of a specific message
                // type; instead we know that resource request messages must contain the 'ResourceID' property at their root level, and that
                // resource response messages must contain a 'Request' property that contains a request w/ the 'ResourceID' property. As such we
                // use those assumptions to try to determine the resource ID. If a message doesn't meet either of this requirements, then it's not
                // a supported resource request or response message, and therefore has no resource ID.
                var resourceID = message.ResourceID;
                if (resourceID === undefined && message.Request !== undefined) {
                    resourceID = message.Request.ResourceID;
                }

                // If the message had a resource ID, only consider it if the resource is registered or if the resource is the app itself
                if (resourceID !== undefined) {
                    shouldHandleMessage = appletInteraction.registeredResources.containsKey(resourceID) || resourceID === SendAppResourceRequestAppID;
                }
            }
        }

        return shouldHandleMessage;
    }

    /** Resets the cache of registered applets and resources. */
    export function resetRegistrations(): void {
        appletInteraction.registeredApplets = [];
        appletInteraction.registeredResources = new util.Map();

        for (var resourceKey in appletInteraction.WellKnownResources) {
            var resource = appletInteraction.WellKnownResources[resourceKey];
            appletInteraction.registeredResources.add(resource.ID, $format(appletInteraction._ResourceUrlFormat, appState.State.Settings.HostName + resource.Url, resource.ID, appState.State.Language));
        }
    }

    interface CommonRegistrationVersions<TRegistration> {

        /** The common version of the applet. */
        CommonVersion: util.PropertyBag<TRegistration>
    }

    interface CommonRegistrations {

        /** The common applet registrations. */
        Applets: util.PropertyBag<CommonRegistrationVersions<appState.CommonAppletRegistration>>;

        /** The common resource registrations. */
        Resources: util.PropertyBag<CommonRegistrationVersions<appState.CommonResourceRegistration>>;
    }

    interface GetCommonRegistrationsCallback { (registrations: CommonRegistrations): void }

    /** Handles the scoping of the getCommonRegistrations method. */
    class GetCommonRegistrationsScope {

        /** Indicates if retrieving common registrations is currently in progress. */
        public InProgress: boolean;

        /** The retrieved common registrations. */
        public CommonRegistrations: CommonRegistrations;

        /** The getCommonRegistrations callbacks. */
        public Callbacks: GetCommonRegistrationsCallback[];
        constructor() {
            this.InProgress = false;
            this.Callbacks = [];
        }

        /**
         * Handles the getCommonRegistrations callbacks, calling each callback with the provided data.
         * @param data The retrieved common registrations.
         */
        public handleCallbacks(data?: CommonRegistrations) {
            this.InProgress = false;
            this.CommonRegistrations = data;
            this.Callbacks.forEach(function iterateCallbacks(callback, index, array) { callback(data); });
        }
    }
    const getCommonRegistrationsScope = new GetCommonRegistrationsScope();

    /**
     * Retrieves the common applets configuration, and provides the configuration to the provided callback.
     * @param callback The callback to execute after retrieval completes, taking the config as the only parameter, or undefined if retrieval fails.
     */
    export function getCommonRegistrations(callback: GetCommonRegistrationsCallback): void {
        if (getCommonRegistrationsScope.CommonRegistrations) {
            callback(getCommonRegistrationsScope.CommonRegistrations);
        } else {
            getCommonRegistrationsScope.Callbacks.push(callback);
            if (!getCommonRegistrationsScope.InProgress) {
                getCommonRegistrationsScope.InProgress = true;
                $.ajax({
                    dataType: "json",
                    jsonp: false,
                    url: app.pathToRoot + "App/Registrations.json",
                    method: "GET",
                    cache: false,
                    success: function (data, textStatus, jqXHR) {
                        getCommonRegistrationsScope.handleCallbacks(data);
                    },
                    error: function (jqXHR, textStatus, errorThrown) {
                        telemetry.errors.commonAppletConfigRetrievalFailed.add(resources.appletInteraction.commonAppletConfigRetrievalFailed());
                        getCommonRegistrationsScope.handleCallbacks();
                    }
                });
            }
        }
    }

    export interface RetrieveRegistrationsCallback { (successful: boolean, notAuthorized: boolean): void }

    /**
     * Retrieves all registered applets and resources and then calls the provided callback.
     * @param callback The callback to execute after retrieval completes, taking one Boolean parameter indicating the success of the retrieval.
     */
    export function retrieveRegistrations(callback: RetrieveRegistrationsCallback): void {
        var retrievalHandler = new appletInteraction._RegistrationsRetrievalHandler(callback);

        // If applet registrations were included in the server configuration payload, use those registrations; otherwise, retrieve the registrations from the appropriate OData entity
        // simultaneously using the data resource and directly from OData. Note that for backwards compatability, we have to continue to support the data resource concept.
        if (appState.State.ServerConfig.AppletRegistrations !== undefined) {
            retrievalHandler.onAppletRegistrationsRetrieved(true, false, <RetrievedAppletRegistration[]>appState.State.ServerConfig.AppletRegistrations);
        } else {
            var appletRegistrationRequest = new messaging.Requests.GetDataRequest();
            appletRegistrationRequest.EntityName = appletInteraction.AppletRegistrationEntityName;
            appletRegistrationRequest.EntitySetName = appletInteraction.AppletRegistrationEntityCollectionName;
            appletInteraction.sendAppResourceRequest(
                appletMessaging.WellKnownResources.Data,
                appletRegistrationRequest,
                function responseRetrieved(success, response: messaging.Responses.GetDataResponse) {
                    success = success && (!response || response.Status === messaging.ResponseStatus.success as any);
                    retrievalHandler.onAppletRegistrationsRetrieved(
                        success,
                        !success && response && response.FailureStatusCode === 403,
                        response && response.Data);
                });
            appletInteraction._retrieveDataDirectly(appletInteraction.AppletRegistrationEntityCollectionName, retrievalHandler.onAppletRegistrationsRetrieved);
        }

        // If resource registrations were included in the server configuration payload, use those registrations; otherwise, retrieve the registrations from the appropriate OData entity
        // simultaneously using the data resource and directly from OData. Note that for backwards compatability, we have to continue to support the data resource concept.
        if (appState.State.ServerConfig.ResourceRegistrations !== undefined) {
            retrievalHandler.onResourceRegistrationsReceived(true, false, appState.State.ServerConfig.ResourceRegistrations);
        } else {
            var resourceRegistrationRequest = new messaging.Requests.GetDataRequest();
            resourceRegistrationRequest.EntityName = appletInteraction.ResourceRegistrationEntityName;
            resourceRegistrationRequest.EntitySetName = appletInteraction.ResourceRegistrationEntityCollectionName;
            appletInteraction.sendAppResourceRequest(
                appletMessaging.WellKnownResources.Data,
                resourceRegistrationRequest,
                function responseReceived(success, response: messaging.Responses.GetDataResponse) {
                    success = success && (!response || response.Status === messaging.ResponseStatus.success as any);
                    retrievalHandler.onResourceRegistrationsReceived(
                        success,
                        !success && response && response.FailureStatusCode === 403,
                        response && response.Data);
                });
            appletInteraction._retrieveDataDirectly(appletInteraction.ResourceRegistrationEntityCollectionName, retrievalHandler.onResourceRegistrationsReceived);
        }
    }

    /**
     * Retrieves data directly from OData, for the provided entity set. Note, this does not handle paging, so if the data exceeds a single page, only the first page is retrieved.
     * @param entitySetName The entity set from which to retrieve data.
     * @param dataRetrievedCallback The callback to perform once the data is retrieved.
     */
    export function _retrieveDataDirectly<TRegistration>(entitySetName: string, dataRetrievedCallback: (successful: boolean, notAuthorized: boolean, registrations?: TRegistration[]) => void): void {

        // If the app is authenticated via OAuth, attempt to make the service call; otherwise, perform the callback as failed, since the service can't be called directly.
        if (appState.State.AuthContext !== undefined) {

            appState.State.AuthContext.acquireToken(appState.State.ServerConfig.AuthResourceId).then((accessToken: string) => {
                if (accessToken) {
                    const headers: util.PropertyBag<string> = { Authorization: "Bearer " + accessToken };
                    appletInteraction._applyHeaders(headers);
                    const handler = new JQueryThrottlingHandler(
                        () => {
                            telemetry.events.odataCallAttempted.log("", `Registrations-${entitySetName}`, "N/A");
                            $.ajax({
                                dataType: 'json',
                                jsonp: false,
                                url: $format("{0}{1}/{2}", appState.State.Settings.HostName, appState.State.ServerConfig.DataEndpointUrl || "/data", entitySetName),
                                method: 'GET',
                                cache: false,
                                headers: headers,
                                success: function (data) {
                                    dataRetrievedCallback(true, false, data.value);
                                },
                                error: function (jqXHR) {
                                    if (!handler.handleThrottleResponse(jqXHR)) {
                                        dataRetrievedCallback(false, jqXHR && jqXHR.status === 403);
                                    }
                                }
                            });
                        },
                        (activityId: string, waitTime: string, retryAttempt: number, retrying: boolean): void => {
                            telemetry.events.odataCallThrottled.log("", `Registrations-${entitySetName}`, "N/A", "N/A", waitTime, retryAttempt, retrying);
                        }
                    );
                    handler.send();
                } else {
                    dataRetrievedCallback(false, false);
                }
            }, (error: AuthError) => {
                telemetry.errors.oAuthAuthenticationFailed.show(
                    resources.appletInteraction.authenticationError(),
                    "",
                    error.errorCode,
                    {
                        Details: error.errorMessage,
                    });
                dataRetrievedCallback(false, false);
            });
        } else {
            dataRetrievedCallback(false, false);
        }
    }

    /** Handles retrieving applet and resource registrations. */
    export class _RegistrationsRetrievalHandler {

        private appletRegistrationsRetrieved: boolean;
        private resourceRegistrationsRetrieved: boolean;
        private retrievalSuccess: boolean;
        private notAuthorized: boolean;
        private appletRegistrationResponsesRetrieved: number;
        private resourceRegistrationResponsesRetrieved: number;
        private resourcesToUnload: string[];
        private callback: RetrieveRegistrationsCallback;

        /* jshint -W040 */
        /**
         * @param callback The callback to perform once all registrations are retrieved.
         */
        constructor(callback: RetrieveRegistrationsCallback) {
            this.callback = callback;
            this.onAppletRegistrationsRetrieved = this.onAppletRegistrationsRetrieved.bind(this);
            this.onResourceRegistrationsReceived = this.onResourceRegistrationsReceived.bind(this);
            this.appletRegistrationsRetrieved = false;
            this.resourceRegistrationsRetrieved = false;
            this.retrievalSuccess = true;
            this.notAuthorized = false;
            this.appletRegistrationResponsesRetrieved = 0;
            this.resourceRegistrationResponsesRetrieved = 0;
            this.resourcesToUnload = [];
        }
        /* jshint +W040 */

        private checkRetrievalComplete(): void {
            if (this.appletRegistrationsRetrieved && this.resourceRegistrationsRetrieved) {
                this.resourcesToUnload.forEach(function iterateResourcesToUnload(resourceID, index, array) { appletInteraction.unloadResource(resourceID); });
                if (this.retrievalSuccess) {
                    if (appletInteraction.registeredApplets.length === 0) {
                        notificationHandling.addErrorNotification(resources.appletInteraction.noAppletRegistrationsFoundMessageFormat(), "NoAppletRegistrationsRetrieved", { LogTelemetry: true });
                        this.callback(true, false);
                    } else {
                        appletInteraction.appletConfiguration.generateAppletConfigurationDOM(() => { this.callback(true, false); });
                    }
                } else {
                    this.callback(false, this.notAuthorized);
                }
            }
        }

        /**
         * Called when applet registrations are retrieved. If successful, the applet registrations are considered retrieved; if failed, the applet registrations are only considered retrieved if both
         * retrieval approaches (data resource and direct OData) fail.
         * @param success Indicates if the retrieval was successful.
         * @param notAuthorized Indicates if the failure was due to a lack of authorization.
         * @param registrations If successful, these represent that retrieved applet registrations.
         */
        public onAppletRegistrationsRetrieved(success: boolean, notAuthorized: boolean, registrations?: RetrievedAppletRegistration[]): void {
            if (this.appletRegistrationsRetrieved) {
                return;
            }

            this.appletRegistrationResponsesRetrieved++;

            success = success && !notAuthorized;
            this.notAuthorized = this.notAuthorized || notAuthorized;

            if (success) {
                this.appletRegistrationsRetrieved = true;
                appletInteraction.registeredApplets = [];
                let commonAppletRegistrations: appState.CommonAppletRegistration[] = [];

                // Perform validation on the applets' relative URLs and log an error notification if invalid
                $.each(registrations, (index, applet) => {
                    if ((<appState.CommonAppletRegistration>applet).CommonVersion) {
                        commonAppletRegistrations.push(applet);
                    } else if (applet.RelativeUrl.search(appletInteraction.RelativeUrlValidationRegex) === -1) {
                        notificationHandling.addErrorNotification(resources.appletInteraction.invalidRelativeUrlFormat(applet.AppletID, applet.RelativeUrl), "AppletRegistrationHadInvalidUrl", { LogTelemetry: true });
                    } else if (appState.State.CurrentApplication & applet.SupportedApplications) {
                        applet.LocalizedName = applet.Name;
                        applet.Path = appState.State.Settings.HostName + applet.RelativeUrl;
                        appletInteraction.registeredApplets.push(applet);
                    }
                });

                // If any common applet registrations were encountered, process them async
                if (commonAppletRegistrations.length > 0) {
                    this.appletRegistrationsRetrieved = false;
                    appletInteraction.getCommonRegistrations((registrations) => {
                        this.appletRegistrationsRetrieved = true;
                        if (registrations) {
                            $.each(commonAppletRegistrations, (index, applet: RetrievedAppletRegistration) => {
                                var appletConfig = registrations.Applets[applet.AppletID];
                                if (appletConfig !== undefined) {
                                    var appletVersionConfig = appletConfig[applet.CommonVersion];
                                    if (appletVersionConfig !== undefined && (appState.State.CurrentApplication & appletVersionConfig.SupportedApplications)) {
                                        applet.Name = resources.labelResolver.resolveLabel(appletVersionConfig.NameLabel);
                                        applet.LocalizedName = applet.Name;
                                        applet.Description = resources.labelResolver.resolveLabel(appletVersionConfig.DescriptionLabel);
                                        applet.Path = $format("{0}/applets{1}", window.location.origin, appletVersionConfig.Path);
                                        applet.SupportedApplications = appletVersionConfig.SupportedApplications;
                                        appletInteraction.registeredApplets.push(applet);
                                    }
                                }
                            });
                        } else {
                            this.retrievalSuccess = false;
                        }
                        this.checkRetrievalComplete();
                    });
                    return;
                }
            } else if (this.appletRegistrationResponsesRetrieved === 2) {
                this.appletRegistrationsRetrieved = true;
                this.retrievalSuccess = false;
            } else {
                // Return early - not all registration retrieval paths have completed
                return;
            }
            this.checkRetrievalComplete();
        }

        /**
         * Called when resource registrations are retrieved. If successful, the resource registrations are considered retrieved; if failed, the resource registrations are only considered retrieved if both
         * retrieval approaches (data resource and direct OData) fail.
         * @param success Indicates if the retrieval was successful.
         * @param notAuthorized Indicates if the failure was due to a lack of authorization.
         * @param registrations If successful, these represent that retrieved resource registrations.
         */
        public onResourceRegistrationsReceived(success: boolean, notAuthorized: boolean, registrations?: RetrievedResourceRegistration[]): void {
            if (this.resourceRegistrationsRetrieved) {
                return;
            }

            this.resourceRegistrationResponsesRetrieved++;

            success = success && !notAuthorized;

            if (success) {
                this.resourceRegistrationsRetrieved = true;
                var commonResourceRegistrations = [];

                // Perform validation on the resource's relative URL and log an error notification if invalid
                $.each(registrations, (index, resource) => {
                    if (resource.CommonVersion) {
                        commonResourceRegistrations.push(resource);
                    } else if (resource.RelativeUrl.search(appletInteraction.RelativeUrlValidationRegex) === -1) {
                        notificationHandling.addErrorNotification(resources.appletInteraction.invalidRelativeUrlFormat(resource.ResourceID, resource.RelativeUrl), "ResourceRegistrationHadInvalidUrl", { LogTelemetry: true });
                    } else {
                        const registeredUrl = appletInteraction.registeredResources.get(resource.ResourceID);
                        const resourceUrl = $format(appletInteraction._ResourceUrlFormat, appState.State.Settings.HostName + resource.RelativeUrl, resource.ResourceID, appState.State.Language);

                        // If the resource is already registered, but it differs, indicate it should be unloaded
                        if (registeredUrl && registeredUrl !== resourceUrl) {
                            this.resourcesToUnload.push(resource.ResourceID);
                        }

                        // If the resource already existed with the same URL, this has no effect; in any other case, it gets added / updated.
                        appletInteraction.registeredResources.add(resource.ResourceID, resourceUrl);
                    }
                });

                // If any common applet registrations were encountered, process them async
                if (commonResourceRegistrations.length > 0) {
                    this.resourceRegistrationsRetrieved = false;
                    appletInteraction.getCommonRegistrations((registrations) => {
                        this.resourceRegistrationsRetrieved = true;
                        if (registrations) {
                            $.each(commonResourceRegistrations, (index, resource) => {
                                var resourceConfig = registrations.Resources[resource.ResourceID];
                                if (resourceConfig !== undefined) {
                                    var resourcePath = resourceConfig[resource.CommonVersion];
                                    if (resourcePath !== undefined) {
                                        this.resourcesToUnload.push(resource.ResourceID);
                                        appletInteraction.registeredResources.add(resource.ResourceID, $format(appletInteraction._ResourceUrlFormat, $format("{0}/applets{1}", window.location.origin, resourcePath), resource.ResourceID, appState.State.Language));
                                    }
                                }
                            });
                        } else {
                            telemetry.events.resourceRegistrationLoadFailed.log();
                        }
                        this.checkRetrievalComplete();
                    });
                    return;
                }
            } else if (this.resourceRegistrationResponsesRetrieved === 2) {
                this.resourceRegistrationsRetrieved = true;
                telemetry.events.resourceRegistrationLoadFailed.log();
            } else {
                // Return early - not all registration retrieval paths have completed
                return;
            }
            this.checkRetrievalComplete();
        }
    }

    export interface UnloadAppletOptions {

        /** A value indicating whether applets should be unloaded, but their settings retained; defaults to false. */
        softUnload?: boolean;
    }

    /**
     * Unloads all applets currently loaded. This method removes all applet frames, generated applet option content, and unregisters all applets.
     * @param options The options used to handle how applets are unloaded.
     */
    export function unloadAllApplets(options?: UnloadAppletOptions): void {
        $.each(appletInteraction.appletMap.getKeys(), function (index, appletID) {
            appletInteraction.unloadApplet(appletID, options);
        });
    }

    /**
     * Unloads the currently loaded applet corresponding to the provided applet ID. This method removes the applet's frame, the applet's generated
     * content, and unregisters the applet. If the applet is active, the next applet in the stack is activated.
     * @param appletID The ID of the applet to unload.
     * @param options The options used to handle how the applet is unloaded.
     */
    export function unloadApplet(appletID: string, options?: UnloadAppletOptions): void {
        if (options === undefined) {
            options = {};
        }

        var appletInformation = appletInteraction.appletMap.get(appletID);

        if (appletInformation !== undefined) {

            // Make next applet active if the applet to be removed ever made it to the applet stack
            var appletIndex = appletInteraction.appletStack.indexOf(appletInformation);

            if (appletIndex !== -1) {
                appletInteraction.appletStack.splice(appletIndex, 1);
                appletInteraction.activateApplet(appletInteraction.getActiveAppletInformation());
            }

            // Remove Office event subscriptions
            officeInteraction.unsubscribeAllDocumentEvents(appletID);
            officeInteraction.unsubscribeAllBindingEvents(appletID);

            // Remove the applet's frame
            appletInteraction._removeAppletFrame(appletID);

            // Unregister the applet
            appletInteraction.appletMap.remove(appletID);
        }

        // Remove any generated options content corresponding to the applet
        var appletOptionsElement = util.findElementById($format(appletInteraction._IdentifierFormats.AppletOptionsElement, appletID), app.optionsDialog.appletOptionsContent);

        if (util.doesElementExist(appletOptionsElement)) {

            appletOptionsElement.remove();
        }

        // Remove generated selection item corresponding to the applet
        var appletSelectionItem = util.findElementById($format(appletInteraction._IdentifierFormats.AppletSelectionItemElement, appletID));

        if (util.doesElementExist(appletSelectionItem)) {

            appletSelectionItem.remove();
        }

        // The applet title shouldn't receive focus if there aren't multiple applets (not a list)
        if (appletInteraction.appletMap.getKeys().length < 2) {
            app.appletTitle.removeAttr("tabindex");
        }

        // Remove the applet from the settings
        if (options.softUnload !== true) {
            delete appState.State.Settings.Applets[appletID];
            app.saveSettings();
        }

        // Cancel any operations initiated from the applet that are still in progress
        operationHandling.cancelOperations(appletID);
    }

    /**
     * Removes the applet's frame, corresponding to the provided applet ID. This method no-ops if the applet or its frame isn't found.
     * @param appletID The ID of the applet for which to remove the frame.
     */
    export function _removeAppletFrame(appletID: string): void {
        var appletInformation = appletInteraction.appletMap.get(appletID);
        if (appletInformation !== undefined && appletInformation.Frame !== undefined) {
            appletInformation.Frame.remove();
            appletInformation.Frame = undefined;
        }
    }

    /** Unloads all resources currently loaded. This method removes all resource frames and unregisters all resources. */
    export function unloadAllResources(): void {
        $.each(appletInteraction.resourceMap.getKeys(), function (index, resourceID) {
            appletInteraction.unloadResource(resourceID);
        });
    }

    /**
     * Unloads the currently loaded resource corresponding to the provided resource ID. This method removes the resource's frame and unregisters the resource.
     * @param resourceID The ID of the resource to unload.
     */
    export function unloadResource(resourceID: string): void {
        var resourceInformation = appletInteraction.resourceMap.get(resourceID);
        var frame;

        // Remove the resource's frame, if found
        if (resourceInformation !== undefined) {
            if (resourceInformation.Frame !== undefined) {
                frame = resourceInformation.Frame;
            }
            appletInteraction.resourceMap.remove(resourceID);
        } else {
            frame = util.findElementById($format(appletInteraction._IdentifierFormats.ResourceFrame, resourceID), app.resourceDiv);
        }

        if (frame !== undefined && util.doesElementExist(frame)) {
            frame.remove();
        }

        appletInteraction.pendingResourceReadyCallbacks.remove(resourceID);
    }

    /**
     * Represents information about an applet. Information includes the ID of the applet, the applet's title, the applet's options, the frame
     * into which the applet is loaded, and the applet's origin.
     */
    export class AppletInformation {

        /** The ID of the applet for which the information pertains. */
        public AppletID: string;

        /** The applet's title. */
        public Title: string;

        /** The array of options groups representing the applet's options. */
        public Options: appletMessaging.OptionsGroup[];

        /** The iframe into which the applet is loaded. */
        public Frame: JQuery;

        /** The origin of the applet. */
        public Origin: string;

        /**
         * @param appletID The ID of the applet for which the information pertains.
         */
        constructor(appletID: string) {
            this.AppletID = appletID;
        }
    }

    /** Represents information about a resource. Information includes the ID of the resource, the resource's frame, and the resource's origin. */
    export class ResourceInformation {

        /** The ID of the resource for which the information pertains. */
        public ResourceID: string;

        /** The iframe into which the resource is loaded. */
        public Frame: JQuery;

        /** The origin of the resource. */
        public Origin: string;

        /**
         * @param resourceID The ID of the resource for which the information pertains.
         */
        constructor(resourceID: string) {
            this.ResourceID = resourceID;
        }
    }

    // <field type="Number">The number of milliseconds to wait for the applet to be ready.</field>
    export const LoadAppletAppletReadyTimeout = 30000;

    // <field type="Number">The number of milliseconds to wait for configuration to complete.</field>
    export const LoadAppletAppletConfigurationTimeout = 10000;

    /**
     * Loads the applet with the provided ID at the provided URL into a frame and sends a configuration request containing the provided
     * settings. Upon the applet responding with indication it has successfully completed configuration, the applet will be activated
     * and the app will be adjusted to indicate it has an applet loaded.
     * @param appletID The ID of the applet to load.
     * @param url The URL at which the applet is located.
     * @param settings The applet specific settings.
     */
    export function loadApplet(appletID: string, url: string, settings: any): void {

        // Find the applet registration for the provided applet ID
        var appletRegistration;
        $.each(appletInteraction.registeredApplets, function (index, registration) {
            if (registration.AppletID === appletID) {
                appletRegistration = registration;
                return false;
            }
        });

        // This is called when applet configuration times out
        function appletConfigurationTimedOut() {

            telemetry.errors.appletLoadTimedOut.show(resources.appletInteraction.unableToLoadAppletErrorFormat(appletID), { Details: resources.appletInteraction.appletConfigurationTimedOutErrorFormat(appletID) });

            appletInteraction.pendingAppletReadyCallbacks.remove(appletID);
            appletInteraction.unloadApplet(appletID);

            telemetry.operations.loadingApplet.end(appletID, resources.appletInteraction.appletLoadingFailedMessageFormat(appletRegistration.LocalizedName));

            appState.State.AppletsLoadingCount--;
            app.updateUIFromState();
        }

        // If the applet is registered, continue trying to load the applet; otherwise add an error notification
        if (appletRegistration !== undefined) {

            // Begin a load operation for the applet
            telemetry.operations.loadingApplet.begin(appletID, resources.appletInteraction.appletLoadingMessageFormat(appletRegistration.LocalizedName));

            // Adjust state to indicate an additional applet is loaded
            appState.State.AppletsLoadingCount++;
            app.updateUIFromState();

            // If the applet has already been loaded into a frame, remove the frame before proceeding
            appletInteraction._removeAppletFrame(appletID);

            // Add the applet's frame and send a configuration request to the applet
            var appletFrame = appElements.addNewFrame(app.appletDiv, $format(appletInteraction._IdentifierFormats.AppletFrame, appletID), $format("{0}?AppletID={1}", url, appletID));

            var appletReadyVerificationHandle = window.setTimeout(function verifyAppletIsReady() {
                if (appletInteraction.pendingAppletReadyCallbacks.containsKey(appletID)) {
                    appletConfigurationTimedOut();
                }
            }, appletInteraction.LoadAppletAppletReadyTimeout);

            appletInteraction.pendingAppletReadyCallbacks.add(appletID, function appletFrameAdded(appletOrigin) {

                window.clearTimeout(appletReadyVerificationHandle);

                // Create the applet information and register the applet in the internal map
                var appletInformation = new appletInteraction.AppletInformation(appletID);
                appletInformation.Title = appletRegistration.LocalizedName;
                appletInformation.Frame = appletFrame;
                appletInformation.Origin = (appletOrigin || "").toUpperCase();
                appletInteraction.appletMap.add(appletID, appletInformation);

                // Create the configuration request to send to the applet
                var request = new appletMessaging.Requests.AppletConfigurationRequest(
                    appletID,
                    {
                        AppVersion: app.version,
                        ApplicationType: appState.State.CurrentApplication,
                        Settings: settings,
                        Language: appState.State.Language,
                        ServerConfig: appState.State.ServerConfig,
                        SessionStorage: appletInteraction._getAppletSessionStorage(appletID),
                        Features: appState.State.Features
                    });
                var responseReceived = false;

                var appletConfiguredVerificationHandle = window.setTimeout(function verifyAppletIsConfigured() {
                    if (!responseReceived) {
                        appletConfigurationTimedOut();
                    }
                }, appletInteraction.LoadAppletAppletConfigurationTimeout);

                appletInteraction.messageHandler.registerResponseCallback(request.MessageID, function appletConfigurationResponseReceived(response) {
                    window.clearTimeout(appletConfiguredVerificationHandle);
                    appletInteraction._handleAppletConfigurationResponse(response);
                });

                // Send the request, retrying the request as necessary (in case the applet hasn't started handling messages right away)
                appletInteraction.sendMessageToApplet(appletID, request, false);
            });
        } else {
            notificationHandling.addErrorNotification(resources.appletInteraction.appletToLoadNotRegisteredMessageFormat(appletID), "AppletToLoadNotRegistered", { LogTelemetry: true });
        }
    }

    /**
     * Handles an update applet options request. Updates the applet's options and the corresponding applet's options DOM.
     * @param event The message event received from the listener, containing the update applet options request.
     */
    export function handleUpdateAppletOptionsRequest(event: util.AddinMessageEvent<appletMessaging.Requests.UpdateAppletOptionsRequest>): void {
        var request = event.data;
        var appletInformation = appletInteraction.appletMap.get(request.AppletID);
        if (appletInformation !== undefined) {
            $.each(request.Data.UpdatedOptions, function (index, updatedOption) {
                if (updatedOption.GroupIndex >= 0 && updatedOption.GroupIndex < appletInformation.Options.length) {
                    var optionsGroup = appletInformation.Options[updatedOption.GroupIndex];
                    if (updatedOption.OptionIndex >= 0 && updatedOption.OptionIndex < optionsGroup.Options.length) {
                        optionsGroup.Options[updatedOption.OptionIndex].Value = updatedOption.Value;
                    }
                }
            });
            appletInteraction._updateOptionsDOM(appletInformation);
        }
        appletInteraction.sendMessageToApplet(request.AppletID, new appletMessaging.Responses.UpdateAppletOptionsResponse(request));
    }

    /**
     * Handles an applet ready request. Performs the registered pending applet ready callbacks.
     * @param event The message event received from the listener, containing the applet ready request.
     */
    export function _handleAppletReadyRequest(event: util.AddinMessageEvent<appletMessaging.Requests.AppletReadyRequest>): void {
        var appletID = event.data.AppletID;
        var appletReadyCallback = appletInteraction.pendingAppletReadyCallbacks.get(appletID);
        if (appletReadyCallback !== undefined) {
            appletInteraction.pendingAppletReadyCallbacks.remove(appletID);
            appletReadyCallback(event.origin);
        }
    }

    /**
     * Handles the applet configuration response for a given applet. If successful the applet's title will be adjusted, the applet's
     * options will be added to the options dialog, the applet will be made visible, and the app will be adjusted to indicate it
     * contains an applet; if configuration failed an error will be displayed and the applet will be unregistered.
     * @param response The response containing indication of the success of applet configuration and, if successful, the applet specific
                       options that should be added to the options dialog.
     */
    export function _handleAppletConfigurationResponse(response: appletMessaging.Responses.AppletConfigurationResponse): void {

        var appletID = response.Request.AppletID;
        var appletInformation = appletInteraction.appletMap.get(appletID);
        var loadSuccessful = response.Status === appletMessaging.ResponseStatus.success;

        if (loadSuccessful) {

            if (appletInformation !== undefined) {
                appletInformation.Options = response.Data.OptionsGroups;
                appletInteraction._generateOptionsDOM(appletInformation);
                appletInteraction._generateSelectionDOM(appletInformation);
                appletInteraction._completeAppletConfiguration(appletID);
            }
        } else {
            telemetry.errors.appletLoadFailed.show(resources.appletInteraction.unableToLoadAppletErrorFormat(appletID), response.Data.FailureTelemetry as string, { Details: response.FailureMessage });
            appletInteraction.unloadApplet(appletID);
        }

        // End the applet load operation
        var appletName = appletInformation === undefined ? appletID : appletInformation.Title;
        telemetry.operations.loadingApplet.end(appletID, loadSuccessful ? resources.appletInteraction.appletLoadingSucceededMessageFormat(appletName) : resources.appletInteraction.appletLoadingFailedMessageFormat(appletName));

        // Adjust state to indicate one less applet is loading and update the UI
        appState.State.AppletsLoadingCount--;
        app.updateUIFromState();
    }

    /**
     * Updates all applets' options. If an applet's options changed, an options changed request will be sent to the applet with
     * the applet's updated options so the applet can act on the updated options and respond with new settings.
     * The applets new settings will be updated into the settings Object.
     * @param settings The settings object to update.
     * @param callback The callback to execute after all applets with changes have responded.
     */
    export function updateAllAppletsOptions(settings: any, callback: () => void): void {
        var pendingAppletOptionCallbacks = new util.Map();

        function performCallbackIfReady() {
            if (pendingAppletOptionCallbacks.getKeys().length === 0) {
                callback();
            }
        }

        function handleAppletOptionChangeResponse(appletResponse) {
            if (appletResponse) {
                var appletOptionsRecievedHandle = pendingAppletOptionCallbacks.get(appletResponse.Request.AppletID);

                window.clearTimeout(appletOptionsRecievedHandle);
                settings.Applets[appletResponse.Request.AppletID] = appletResponse.Data.Settings;
                pendingAppletOptionCallbacks.remove(appletResponse.Request.AppletID);
            }

            performCallbackIfReady();
        }

        $.each(appletInteraction.appletMap.getKeys(), function (index, appletID) {
            if (appletInteraction._updateAppletOptions(appletID) && appletInteraction.sendAppletOptionsChangedRequest(appletID, handleAppletOptionChangeResponse)) {
                var appletOptionsRecievedHandle = window.setTimeout(
                    function appletOptionChangeTimedOut() {
                        notificationHandling.addErrorNotification(resources.appletInteraction.unableToUpdateAppletOptionsErrorFormat(appletID), "UnableToUpdateAppletOptions", { LogTelemetry: true });
                        pendingAppletOptionCallbacks.remove(appletID);
                        performCallbackIfReady();
                    },
                    appletInteraction.UpdateAppletOptionsTimeout);

                pendingAppletOptionCallbacks.add(appletID, appletOptionsRecievedHandle);
            }
        });

        performCallbackIfReady();
    }

    /**
     * Updates the options for the applet corresponding to the provided ID.
     * @param appletID The ID of the applet for which to update applet specific options.
     * @returns A value indicating if options were changed.
     */
    export function _updateAppletOptions(appletID: string): boolean {
        var optionsChanged = false;

        // Get information about the applet
        var appletInformation = appletInteraction.appletMap.get(appletID);

        // If the applet information is found, continue the process of updating its options
        if (appletInformation !== undefined) {

            // Find the generated applet options element
            var appletOptionsElement = util.findElementById($format(appletInteraction._IdentifierFormats.AppletOptionsElement, appletInformation.AppletID), app.optionsDialog.appletOptionsContent);

            // If the generated applet options element was found, continue the process of updating its options; it's completely valid to not have any applet
            // specific options generated for an applet
            if (appletOptionsElement.length === 1) {

                // Iterate over each of the applet's options groups, updating the options contained within
                $.each(appletInformation.Options, function (optionsGroupIndex, optionsGroup) {

                    // Iterate over each of the applet options, updating the value from the generated control's value and determining if the value changed
                    $.each(optionsGroup.Options, function (optionIndex, option) {

                        // Find the generated option control
                        var optionID = $format(appletInteraction._IdentifierFormats.AppletOptionControl, appletInformation.AppletID, optionsGroupIndex, optionIndex);
                        var optionControl = util.findElementById(optionID, appletOptionsElement);

                        var currentValue;

                        switch (option.Type) {
                            case appletMessaging.OptionInformationType.String:
                                // Use the value of a normal input control as the current value
                                currentValue = optionControl.val();
                                break;
                            case appletMessaging.OptionInformationType.Boolean:
                                // Use the checked property of a checkbox control as the current value
                                currentValue = optionControl.prop("checked");
                                break;
                            case appletMessaging.OptionInformationType.Action:
                            case appletMessaging.OptionInformationType.DisplayOnly:
                                // Actions and display only controls don't have values that can change, so just use the option value as the current value
                                currentValue = option.Value;
                                break;
                        }

                        // Determine if the option's value changed and update the option's value to the current value
                        optionsChanged = optionsChanged || option.Value !== currentValue;
                        option.Value = currentValue;
                    });
                });
            }
        }

        return optionsChanged;
    }

    /**
     * Gets the session storage corresponding to the provided applet.
     * @param appletID The ID of the applet for which to retrieve the session storage.
     * @returns The session storage corresponding to the provided applet.
     */
    export function _getAppletSessionStorage(appletID: string): util.PropertyBag<any> {
        const appletStorageValue = sessionStorage[appletID];
        let appletSessionStorage: util.PropertyBag<any>;
        if (appletStorageValue) {
            try {
                appletSessionStorage = JSON.parse(appletStorageValue);
            } catch (e) { }
        }
        return appletSessionStorage;
    }

    /**
     * Sets the key / value pair into the provided applet's session storage. If the value is null or undefined, the request
     * is considered a delete request and the item is removed from the provided applet's session storage.
     * @param appletID The ID of the applet for which to add or remove the key / value pair.
     * @param key The key of the item to add / remove.
     * @param value The value to add.
     */
    export function _setAppletItemToSessionStorage(appletID: string, key: string, value: string): void {
        if (key) {
            const appletSessionStorage = appletInteraction._getAppletSessionStorage(appletID) || {};
            try {
                if (value === null || value === undefined) {
                    delete appletSessionStorage[key];
                } else {
                    appletSessionStorage[key] = value;
                }
                sessionStorage[appletID] = JSON.stringify(appletSessionStorage);
            } catch (e) { }
        }
    }

    /**
     * Generates the selection DOM for the provided applet's information, which will enable switching to the provided applet.
     * @param appletInformation The information for the applet for which to generate a selection item.
     */
    export function _generateSelectionDOM(appletInformation: appletInteraction.AppletInformation): void {

        // Create the applet selection item element and append it to the applet selection list
        $(
            "<li></li>",
            {
                id: $format(appletInteraction._IdentifierFormats.AppletSelectionItemElement, appletInformation.AppletID),
                on: {
                    click: function appletSelectionItemClicked() {
                        appletInteraction.activateApplet(appletInformation);
                    }
                }
            })
            .append($("<span></span>", { text: appletInformation.Title }))
            .appendTo(app.appletSelectionList);

        // The applet title should receive focus if there are multiple applets (a list)
        if (appletInteraction.appletMap.getKeys().length > 1) {
            app.appletTitle.attr("tabindex", 0);
        }
    }



    /** The selector used to retrieve an applet's option section header. */
    const GenerateOptionsDOMAppletOptionSectionHeaderSelector = "h2";

    /**
     * Generates the options DOM for the provided applet's information, where option group elements with be generated for each of the
     * applet information's options groups using their label as the option group heading, and an input control with corresponding markup
     * will be generated for each option.
     * @param appletInformation The information for the applet for which to generate applet specific option content.
     */
    export function _generateOptionsDOM(appletInformation: appletInteraction.AppletInformation): void {

        // If there are options defined for the applet, generate the appropriate options DOM
        if (appletInformation.Options !== undefined && appletInformation.Options.length > 0) {

            const sectionContainerID = `${appletInformation.AppletID}_SectionContainer`;

            // Create the applet options element that will contain all the applet's specific options
            var appletOptionsElement = $(
                "<div></div>",
                {
                    id: $format(appletInteraction._IdentifierFormats.AppletOptionsElement, appletInformation.AppletID),
                    "class": "optionSection"
                })
                .append($(
                    "<h2></h2>",
                    {
                        "class": "optionsHeader",
                    })
                    .append($(
                        "<button></button>",
                        {
                            "aria-expanded": true,
                            text: appletInformation.Title,
                            "aria-controls": sectionContainerID,
                            on: {
                                click: app.toggleOptionSection
                            }
                        })))
                .append($("<div></div>", { id: sectionContainerID, "class": "optionSectionContainer" }))
                .appendTo(app.optionsDialog.appletOptionsContent)
                .find("div");

            // Iterate over each of the applet's options groups, generating appropriate content
            $.each(appletInformation.Options, function (optionsGroupIndex, optionsGroup) {

                // Create the options group element that will contain the options group options, and will use the options group's label as the heading
                var optionGroupElement = $("<div></div>").append($("<h3></h3>", { text: optionsGroup.Label })).appendTo(appletOptionsElement);

                // Iterate over each of the options group's options, generating appropriate content
                $.each(optionsGroup.Options, function (optionIndex, option) {

                    // Determine the ID that will be used for the option's control
                    var optionID = $format(appletInteraction._IdentifierFormats.AppletOptionControl, appletInformation.AppletID, optionsGroupIndex, optionIndex);

                    switch (option.Type) {
                        case appletMessaging.OptionInformationType.String:
                            // Generate a normal input control for string options, and set the value to the option's value
                            $("<div></div>")
                                .append($("<label></label>", { for: optionID, text: option.Label }))
                                .append(
                                    $(
                                        "<input></input>",
                                        {
                                            id: optionID,
                                            "class": "stringOptionInput",
                                            val: option.Value
                                        }))
                                .appendTo(optionGroupElement);
                            break;
                        case appletMessaging.OptionInformationType.Boolean:
                            // Generate a checkbox control for boolean options, and set the checked property based on the option's value
                            $("<div></div>")
                                .append(
                                    $(
                                        "<input></input>",
                                        {
                                            type: "checkbox",
                                            id: optionID,
                                            "class": "inlineControl",
                                            prop: { checked: option.Value }
                                        }
                                    ))
                                .append($("<label></label>", { for: optionID, "class": "inlineControl", text: option.Label }))
                                .appendTo(optionGroupElement);
                            break;
                        case appletMessaging.OptionInformationType.Action:
                            // Generate a button control for action options, and set the click handler to perform the action set in the option's value
                            $(
                                "<button></button>",
                                {
                                    id: optionID,
                                    text: option.Label,
                                    title: resources.dynamicsApp.optionsAppletButtonTitleFormat(option.Label),
                                    on: {
                                        click: function optionActionButtonClicked() {
                                            appletInteraction.sendAppletActionRequest(appletInformation.AppletID, option.Value);
                                        }
                                    }
                                })
                                .appendTo(optionGroupElement);
                            break;
                        case appletMessaging.OptionInformationType.DisplayOnly:
                            // Generate a normal input control for string options, and set the value to the option's value
                            $("<div></div>")
                                .append($("<label></label>", { for: optionID, text: option.Label }))
                                .append(
                                    $(
                                        "<input></input>",
                                        {
                                            id: optionID,
                                            "class": "stringOptionInput",
                                            readonly: true,
                                            val: option.Value
                                        }))
                                .appendTo(optionGroupElement);
                            break;
                    }
                });
            });
        }
    }

    /* Updates the options DOM for each of the applets. */
    export function updateAllAppletOptionsDOMs(): void {
        $.each(appletInteraction.appletMap.getKeys(), function (index, appletID) {
            var appletInformation = appletInteraction.appletMap.get(appletID);

            // If the applet information is found, continue the process of updating its options DOM
            if (appletInformation !== undefined) {
                appletInteraction._updateOptionsDOM(appletInformation);
            }
        });
    }

    /**
     * Updates the options DOM for the provided applet's information.
     * @param appletInformation The information for the applet for which to update applet specific option content.
     */
    export function _updateOptionsDOM(appletInformation: appletInteraction.AppletInformation): void {
        var appletOptionsElement = util.findElementById($format(appletInteraction._IdentifierFormats.AppletOptionsElement, appletInformation.AppletID), app.optionsDialog.appletOptionsContent);

        // If the previously generated applet options element was found, continue the process of updating its options; it's completely valid to not have any applet
        // specific options generated for an applet
        if (util.doesElementExist(appletOptionsElement)) {

            // Iterate over each of the applet's options groups, updating the options contained within
            $.each(appletInformation.Options, function (optionsGroupIndex, optionsGroup) {

                // Iterate over each of the applet options, updating the value from the generated control's value and determining if the value changed
                $.each(optionsGroup.Options, function (optionIndex, option) {

                    // Find the generated option control
                    var optionID = $format(appletInteraction._IdentifierFormats.AppletOptionControl, appletInformation.AppletID, optionsGroupIndex, optionIndex);
                    var optionControl = util.findElementById(optionID, appletOptionsElement);

                    switch (option.Type) {
                        case appletMessaging.OptionInformationType.String:
                            // Set the value to the option's value
                            optionControl.val(option.Value);
                            break;
                        case appletMessaging.OptionInformationType.Boolean:
                            // Set the checked property based on the option's value
                            optionControl.prop("checked", option.Value);
                            break;
                        case appletMessaging.OptionInformationType.Action:
                            // Actions don't have a value that can change, so no update is necessary.
                            break;
                    }
                });
            });
        }
    }

    /**
     * Enables the applet corresponding to the provided applet ID. It marks the applet as ready and makes it active if
     * there are no other applets already active.
     * @param appletID The ID of the applet to enable.
     */
    export function _completeAppletConfiguration(appletID: string): void {
        var foundApplet;
        var otherAppletActive = false;

        // Iterate over all applets, making the provided applet ready and determine if any other applets are already active
        $.each(appletInteraction.appletMap.getKeys(), function (index, id) {

            // Get the applet information for the current applet
            var appletInformation = appletInteraction.appletMap.get(id);

            // If the applet information was found for the current applet, and it has a frame, continue work to make current applet
            // ready and determine if other applets are already active
            if (appletInformation !== undefined && appletInformation.Frame !== undefined) {

                // If the applet information is for the provided applet ID, mark it as ready;
                // otherwise determine if the applet is already active
                if (id === appletID) {
                    foundApplet = appletInformation;
                } else if (!otherAppletActive) {
                    var isActiveValue = appletInformation.Frame.attr(appElements.GeneralAttributes.isActive);
                    otherAppletActive = isActiveValue !== undefined && isActiveValue.toString().toLowerCase() === "true";
                }
            }
        });

        // If the applet information for the provided applet was found add it to the applet stack
        if (foundApplet !== undefined) {

            appletInteraction.appletStack.push(foundApplet);

            // If no other applets are active make the provided applet active
            if (!otherAppletActive) {
                appletInteraction.activateApplet(foundApplet);
            } else {
                appState.State.AppletsReadyState = appletInteraction.getAppletsReadyState();
                app.updateUIFromState();
            }
        }

        // If the applet doesn't already have settings, add an undefined entry in the settings
        if (!(appletID in appState.State.Settings.Applets)) {
            appState.State.Settings.Applets[appletID] = {};
            app.saveSettings();
        }
    }

    /**
     * Activates an applet, setting all other applets as inactive, and sets app's has applets state.
     * @param appletInformation The information for the applet to activate.
     */
    export function activateApplet(appletInformation: appletInteraction.AppletInformation): void {
        var appletTitle = "";
        var appletStackLength = appletInteraction.appletStack.length;

        // If there is an applet to activate, progress the workflow of activating the applet
        if (appletInformation !== undefined) {

            // Set the app's applet title to the applet's title
            appletTitle = appletInformation.Title;

            // Get the active applet
            var activeApplet = appletInteraction.getActiveAppletInformation();

            // If the applet isn't already the active applet, then deactivate the current active applet
            if (activeApplet !== appletInformation) {

                // If the provided applet is already on the stack, remove it from the stack
                var appletIndex = appletInteraction.appletStack.indexOf(appletInformation);
                if (appletIndex === -1) {
                    appletStackLength++;
                } else {
                    appletInteraction.appletStack.splice(appletIndex, 1);
                }

                // Add the provided applet to the top of the stack (making it active)
                appletInteraction.appletStack.splice(0, 0, appletInformation);

                // If there was a previously active applet, adjust its DOM elements to indicate it is no longer active
                if (activeApplet !== undefined) {

                    // Mark the former active applet's frame as inactive
                    activeApplet.Frame.attr(appElements.GeneralAttributes.isActive, "false");

                    // Mark the former active applet's selection item as inactive
                    util.findElementById($format(appletInteraction._IdentifierFormats.AppletSelectionItemElement, activeApplet.AppletID)).attr(appElements.GeneralAttributes.isActive, "false");
                }
            }

            // Mark the provided applet's frame as active
            appletInformation.Frame.attr(appElements.GeneralAttributes.isActive, "true");

            // Mark the provided applet's selection item as active
            util.findElementById($format(appletInteraction._IdentifierFormats.AppletSelectionItemElement, appletInformation.AppletID)).attr(appElements.GeneralAttributes.isActive, "true");
        }

        // Set the app's applet title to the determined applet title
        app.appletTitle.text(appletTitle);

        // Adjust the app's applets ready state to reflect the number of applets that are currently ready
        appState.State.AppletsReadyState = appletInteraction.getAppletsReadyState();
        app.updateUIFromState();

        // Set the active applet in the settings as the applet on top of the stack and save the settings
        appState.State.Settings.ActiveAppletId = appletInformation !== undefined ? appletInformation.AppletID : undefined;
        app.saveSettings();
    }

    /**
     * Gets the information about the currently active applet.
     * @returns The information about the currently active applet, or undefined if there is no active applet.
     */
    export function getActiveAppletInformation(): appletInteraction.AppletInformation {
        return appletInteraction.appletStack.length > 0 ? appletInteraction.appletStack[0] : undefined;
    }

    /**
     * Gets the current ready applets state, based on the number of ready applets.
     * @returns The current ready applets state.
     */
    export function getAppletsReadyState(): appState.AppletsReadyState {
        var state;
        switch (appletInteraction.appletStack.length) {
            case 0:
                state = appState.AppletsReadyState.None;
                break;
            case 1:
                state = appState.AppletsReadyState.Single;
                break;
            default:
                state = appState.AppletsReadyState.Multiple;
                break;
        }
        return state;
    }

    /**
     * Sends a request to the applet corresponding to the provided applet ID indicating its options have changed, providing the applet's current options.
     * @param appletID The ID of the applet to send the options changed request to.
     * @param callback The callback to execute after the applet has responded to the options change request.
     * @returns True if a request was sent to the applet; otherwise, false.
     */
    export function sendAppletOptionsChangedRequest(appletID: string, callback: (response: appletMessaging.Responses.AppletOptionsChangedResponse) => void): boolean {
        var requestSent = false;

        // Get the applet information for the provided applet ID
        var appletInformation = appletInteraction.appletMap.get(appletID);

        // If the applet information was found, send an options changed request to the applet with the applet's current options
        if (appletInformation !== undefined) {
            var request = new appletMessaging.Requests.AppletOptionsChangedRequest(appletID, appletInformation.Options);
            appletInteraction.messageHandler.registerResponseCallback(request.MessageID, callback);
            appletInteraction.sendMessageToApplet(appletID, request);
            requestSent = true;
        }

        return requestSent;
    }

    /**
     * Handles a save settings request received from an applet. It saves the settings contained within the received request
     * back into the document's settings, keyed under the applet ID. Upon the save operation completing, a response is sent
     * back to the applet indicating if the save operation completed successfully.
     * @param event The message event received from the listener, containing the save settings request.
     */
    export function handleSaveSettingsRequest(event: util.AddinMessageEvent<appletMessaging.Requests.SaveSettingsRequest>): void {
        var request = event.data;
        var settings = request.Data.Settings;

        if (settings === undefined) {
            settings = {};
        }

        // Apply the applet's settings to the app's settings
        appState.State.Settings.Applets[request.AppletID] = settings;

        // Save the settings and send back a response indicating the success of the save operation to the applet, once the operation completes
        app.saveSettings(function saveSettingsCallback(asyncResult) {
            var response = new appletMessaging.Responses.SaveSettingsResponse(request);
            response.Status = asyncResult.status === Office.AsyncResultStatus.Succeeded ? appletMessaging.ResponseStatus.success : appletMessaging.ResponseStatus.failure;
            response.FailureMessage = asyncResult.error === undefined ? undefined : asyncResult.error.message;

            appletInteraction.sendMessageToApplet(request.AppletID, response);
        });
    }

    /**
     * Handles a set session storage item request from an applet. It sets the item into the applet's session storage appropriately.
     * This method doesn't send a response.
     * @param event The message event received from the listener, containing the set session storage item request.
     */
    export function handleSetSessionStorageItemRequest(event: util.AddinMessageEvent<appletMessaging.Requests.SetSessionStorageItemRequest>): void {
        const request = event.data;
        appletInteraction._setAppletItemToSessionStorage(request.AppletID, request.Data.Key, request.Data.Value);
    }

    /**
     * Sends a request to the provided applet to perform the action corresponding to the provided action name.
     * @param appletID The ID of the applet that should perform the action.
     * @param actionName The name of the action that the applet should perform.
     */
    export function sendAppletActionRequest(appletID: string, actionName: string): void {
        var request = new appletMessaging.Requests.AppletActionRequest(appletID, actionName);
        appletInteraction.sendMessageToApplet(appletID, request);
    }

    /**
     * Handles the appletMessaging.Requests.AddNotificationRequest event and adds or shows a notification or error accordingly.
     * @param event The request event.
     */
    export function handleAddNotificationRequest(event: util.AddinMessageEvent<appletMessaging.Requests.AddNotificationRequest>): void {
        var request = event.data;
        var notification = request.Data.Notification;

        if (notification.LinkOptions !== undefined) {
            var action: (() => void) = $.noop;
            var actionText = notification.LinkOptions.Text;
            if ("Action" in notification.LinkOptions) {
                var actionName = notification.LinkOptions.Action as string;
                action = function () {
                    appletInteraction.sendAppletActionRequest(request.AppletID, actionName);
                };
            } else if ("AppAction" in notification.LinkOptions) {
                switch (notification.LinkOptions.AppAction) {
                    case appletMessaging.AppActions.OpenMessageCenter:
                        action = notificationHandling.handler._notificationsButtonClicked;
                        actionText = actionText || resources.dynamicsApp.notificationsButtonTooltip();
                        break;
                    case appletMessaging.AppActions.OpenSettings:
                        action = app.optionsDialog.openWithDataConnectorOptionsExpanded;
                        actionText = actionText;
                        break;
                }
            }
            notification.LinkOptions.Action = action;
            notification.LinkOptions.Text = actionText;
        }

        if (notification.Type === appletMessaging.NotificationType.error) {
            if (notification.DisplayToUser) {
                notificationHandling.showError(notification.Message, notification.ErrorCode, <any>$.extend(true, { SourceID: request.AppletID }, notification));
            } else {
                notificationHandling.addErrorNotification(notification.Message, notification.ErrorCode, $.extend(true, { LogTelemetry: true, SourceID: request.AppletID }, notification) as notificationHandling.AddErrorNotificationOptions);
            }
        } else {
            if (notification.DisplayToUser) {
                notificationHandling.showNotification(notification.Message, (notification as any) as notificationHandling.ShowNotificationOptions);
            } else {
                notificationHandling.addNotification(notification.Message, notification.IncludeTimeStamp);
            }
        }
    }

    /**
     * Handles the appletMessaging.Requests.PromptUserRequest event and prompts the user for a "Yes"/"No" response.
     * @param event The request event.
     */
    export function handlePromptUserRequest(event: util.AddinMessageEvent<appletMessaging.Requests.PromptUserRequest>): void {
        var callbackHandler = new appletInteraction.HandlePromptUserRequestCallbackHandler(event);
        notificationHandling.showConfirmationDialog(
            event.data.Data.Message,
            callbackHandler._sendPromptUserResponse,
            {
                Details: event.data.Data.Details,
                SourceID: event.data.AppletID
            });
    }

    /** Handles callbacks for the appletInteraction.handlePromptUserRequest method. */
    export class HandlePromptUserRequestCallbackHandler {

        /** The request event. */
        private event: util.AddinMessageEvent<appletMessaging.Requests.PromptUserRequest>;

        /**
         * @param event The request event.
         */
        constructor(event: util.AddinMessageEvent<appletMessaging.Requests.PromptUserRequest>) {
            this.event = event;
            this._sendPromptUserResponse = this._sendPromptUserResponse.bind(this);
        }

        /**
         * Sends the user's response to the applet.
         * @param confirmation The user's response to the prompt (true for "Yes" and false for "No").
         */
        public _sendPromptUserResponse(confirmation: boolean): void {
            var response = new appletMessaging.Responses.PromptUserResponse(this.event.data, confirmation);
            response.Status = appletMessaging.ResponseStatus.success;
            appletInteraction.sendMessageToApplet(this.event.data.AppletID, response);
        }
    }

    /**
     * Handles the appletMessaging.Requests.LogEventRequest event and logs the specified event.
     * @param event The request event.
     */
    export function handleLogEventRequest(event: util.AddinMessageEvent<appletMessaging.Requests.LogEventRequest>): void {
        telemetryLogging.logEvent(event.data.Data.Event, { SourceID: event.data.AppletID, MessageType: event.data.Data.MessageType, TelemetryType: event.data.Data.TelemetryType, TelemetryDetails: event.data.Data.TelemetryDetails });
    }

    /**
     * Handles the appletMessaging.Requests.BeginOperationRequest event and starts the specified operation.
     * @param event The request event.
     */
    export function handleBeginOperationRequest(event: util.AddinMessageEvent<appletMessaging.Requests.BeginOperationRequest>): void {
        operationHandling.beginOperation(event.data.Data.OperationID, event.data.Data.Message, { SourceID: event.data.AppletID });
    }

    /**
     * Handles the appletMessaging.Requests.UpdateOperationRequest event and starts the specified operation.
     * @param event The request event.
     */
    export function handleUpdateOperationRequest(event: util.AddinMessageEvent<appletMessaging.Requests.UpdateOperationRequest>): void {
        operationHandling.updateOperation(event.data.Data.OperationID, event.data.Data.Message, { SourceID: event.data.AppletID });
    }

    /**
     * Handles the appletMessaging.Requests.EndOperationRequest event and ends the specified operation.
     * @param event The request event.
     */
    export function handleEndOperationRequest(event: util.AddinMessageEvent<appletMessaging.Requests.EndOperationRequest>): void {
        operationHandling.endOperation(event.data.Data.OperationID, event.data.Data.Message, { IncludeTimeStamp: event.data.Data.IncludeTimeStamp, SourceID: event.data.AppletID, TelemetryDetails: event.data.Data.TelemetryDetails });
    }

    /**
     * Handles a (non-operational) set status event, displaying the provided status if no operations are active.
     * @param event The request event.
     */
    export function handleSetStatusRequest(event: util.AddinMessageEvent<appletMessaging.Requests.SetStatusRequest>): void {
        operationHandling.setStatus(event.data.Data.Message);
    }

    /**
     * Handles the appletMessaging.Requests.OpenOptionsRequest event and opens the options dialog.
     * @param event The request event.
     */
    export function handleOpenOptionsRequest(event: util.AddinMessageEvent<appletMessaging.Requests.OpenOptionsRequest>): void {
        app.optionsDialog.open();
    }

    /**
     * Handles the appletMessaging.Requests.CloseOptionsRequest event and closes the options dialog.
     * @param event The request event.
     */
    export function handleCloseOptionsRequest(event: util.AddinMessageEvent<appletMessaging.Requests.CloseOptionsRequest>): void {
        app.optionsDialog.close();
    }

    /**
     * Handles a change connection address request, determining if the provided connection address is valid, and prompting the user.
     * If the connection is valid and the user confirms they want to change connections, a response is sent indicating the add-in
     * will reconnect, after which the add-in reloads; otherwise, if the connection was blank, invalid, the same as the current connection
     * address, or the user declines to change the connection, a response is sent back to the applet indicating the add-in won't reconnect
     * and no reconnection occurs.
     * @param event The message event received from the listener, containing the change connection address request.
     */
    export function handleChangeConnectionAddressRequest(event: util.AddinMessageEvent<appletMessaging.Requests.ChangeConnectionAddressRequest>): void {

        const request = event.data;

        function sendResponse(willRedirect: boolean): void {
            const response = new appletMessaging.Responses.ChangeConnectionAddressResponse(request, willRedirect);
            response.Status = appletMessaging.ResponseStatus.success;
            appletInteraction.sendMessageToApplet(request.AppletID, response, false);
            telemetry.events.reconnectionRequested.log(willRedirect);
        }

        const connectionResults = app.setConnection(request.Data.ConnectionAddress, {}, { AllowEmptyConnection: false });
        if (connectionResults.AppOptionsAreValid) {
            if (connectionResults.AppReloadRequired) {
                notificationHandling.showConfirmationDialog(
                    resources.appletInteraction.addinReconnectMessage(appState.State.Settings.HostName, request.Data.ConnectionAddress),
                    (userResponse) => {
                        if (userResponse) {
                            const currentHostName = appState.State.Settings.HostName;
                            appState.State.Settings.HostName = request.Data.ConnectionAddress;
                            app.saveSettings((result) => {
                                const saveSuccessful = result.status === Office.AsyncResultStatus.Succeeded;
                                sendResponse(saveSuccessful);
                                if (saveSuccessful) {
                                    window.setTimeout(() => { window.location.reload(); }, 250);
                                } else {
                                    appState.State.Settings.HostName = currentHostName;
                                }
                            });
                        } else {
                            sendResponse(false);
                        }
                    },
                    {
                        SourceID: request.AppletID
                    });
                return; // Return early, so message isn't sent to applet while waiting for user's response
            } else {
                notificationHandling.showNotification(resources.appletInteraction.connectionNotChangedMessage());
            }
        }
        sendResponse(false);
    }

    /** The number of milliseconds to wait before timing out waiting for a resource to load. */
    const HandleResourceRequestResourceLoadTimeout = 30000;

    /**
     * Tries to handle the provided message. If the provided message isn't an OData call - refresh headers message
     * then this method returns false.
     * @param resourceResponse The message to attempt to handle.
     * @param appletID The ID of the originating applet.
     * @param resourceID The ID of the related resource.
     * @param messageID The ID of the message.
     * @returns A value indicate whether the provided message was an OData call - refresh headers message.
     */
    export function tryHandleODataRefreshHeadersMessage(
        resourceResponse: messaging.BaseResponse<messaging.BaseRequest>,
        appletID: string,
        resourceID: string,
        messageID: string): boolean {
        let messageHandled = false;
        if (resourceResponse.Type === messaging.MessageTypes.odataCallRefreshHeaders) {
            const authorizationHeader = (resourceResponse.Request as (messaging.BaseRequest & { Headers: any })).Headers.Authorization;
            appletInteraction.enhanceResourceRequest(resourceResponse.Request, resourceID, messageID, () => {
                if ((resourceResponse.Request as (messaging.BaseRequest & { Headers: any })).Headers.Authorization !== authorizationHeader) {
                    window.setTimeout(() => telemetry.events.odataCallAuthTokenUpdated.log(appletID, resourceResponse.Request.Type));
                }
                appletInteraction.sendMessageToResource(resourceID, resourceResponse as messaging.Responses.ODataCallRefreshHeadersMessage<messaging.BaseRequest>, false);
            });
            messageHandled = true;
        }
        return messageHandled;
    }

    /**
     * Logs OData correlation telemetry as appropriate.
     * @param resourceID The ID of the resource from which the response was received.
     * @param response The response that was received.
     * @param sourceID The ID of the originating source.
     */
    function logODataCorrelationTelemetry(resourceID: string, response: messaging.BaseResponse<messaging.BaseRequest>, sourceID?: string): void {
        if (resourceID === appletMessaging.WellKnownResources.Data) {
            let odataResponse = <messaging.Responses.ODataResponse<messaging.BaseRequest>>response;
            if (response.Type === messaging.MessageTypes.odataCallAttempted) {
                if (odataResponse.Activities && odataResponse.Activities.length) {
                    for (const activity of odataResponse.Activities) {
                        telemetry.events.odataCallAttempted.log(
                            sourceID,
                            odataResponse.Request.Type,
                            activity.ID,
                            activity.RecordCount);
                    }
                } else if (odataResponse.ActivityIDs && odataResponse.ActivityIDs.length) {
                    for (const activityID of odataResponse.ActivityIDs) {
                        telemetry.events.odataCallAttempted.log(
                            sourceID,
                            odataResponse.Request.Type,
                            activityID);
                    }
                }
            } else if (response.Type === messaging.MessageTypes.odataCallThrottled) {
                let throttledResponse = <messaging.Responses.ODataCallThrottledResponse<messaging.BaseRequest>>response;
                if (odataResponse.Activities && odataResponse.Activities.length) {
                    for (const activity of odataResponse.Activities) {
                        telemetry.events.odataCallThrottled.log(
                            sourceID,
                            odataResponse.Request.Type,
                            activity.ID,
                            throttledResponse.OriginalActivityId,
                            throttledResponse.WaitTime,
                            throttledResponse.RetryAttempt,
                            throttledResponse.Retrying,
                            activity.RecordCount);
                    }
                } else if (odataResponse.ActivityIDs && odataResponse.ActivityIDs.length) {
                    for (const activityID of odataResponse.ActivityIDs) {
                        telemetry.events.odataCallThrottled.log(
                            sourceID,
                            odataResponse.Request.Type,
                            activityID,
                            throttledResponse.OriginalActivityId,
                            throttledResponse.WaitTime,
                            throttledResponse.RetryAttempt,
                            throttledResponse.Retrying);
                    }
                }
            } else {
                if ($.isArray(odataResponse.Activities)) {
                    $.each(odataResponse.Activities, (index, activity) => {
                        const odataCallArgs = [
                            sourceID,
                            odataResponse.Request.Type,
                            activity.ID,
                            odataResponse.Status,
                            odataResponse.FailureStatusCode,
                            activity.Duration
                        ];
                        if (activity.RecordCount !== undefined) {
                            odataCallArgs.push(activity.RecordCount);
                        }
                        telemetry.events.odataCall.log.apply(telemetry.events.odataCall.log, odataCallArgs);
                    });
                } else if ($.isArray(odataResponse.ActivityIDs)) {
                    $.each(odataResponse.ActivityIDs, (index, activityID) => {
                        telemetry.events.odataCall.log(
                            sourceID,
                            odataResponse.Request.Type,
                            activityID,
                            odataResponse.Status,
                            odataResponse.FailureStatusCode);
                    });
                }
            }
        }
    }


    /**
     * Sends the provided request, which is initiated from the app to a resource corresponding to the provided
     * resource ID. Upon response from the resource, the provided callback is called.
     * @param resourceID The ID of the resource to send the provided request to.
     * @param request The request to send to the resource corresponding to the provided resource ID.
     * @param callback The callback to perform once a response is received from the provided resource.
     */
    export function sendAppResourceRequest(resourceID: string, request: messaging.BaseRequest, callback: (successful: boolean, response?: messaging.BaseResponse<messaging.BaseRequest>) => void): void {
        var resourceInformation = appletInteraction.resourceMap.get(resourceID);

        // The function to call once the resource is loaded, which will progress the workflow of communicating with the resource
        function progressResourceRequest() {
            var messageID = appletMessaging.createUniqueMessageID(SendAppResourceRequestAppID, resourceID);

            appletInteraction.enhanceResourceRequest(request, resourceID, messageID, function requestEnhanced() {
                appletInteraction.sendMessageToResource(resourceID, request, false);
                appletInteraction.messageHandler.registerResponseCallback(messageID, function handleAppResourceResponse(response: messaging.BaseResponse<messaging.BaseRequest>) {
                    if (!tryHandleODataRefreshHeadersMessage(response, undefined, resourceID, messageID)) {
                        window.setTimeout(() => logODataCorrelationTelemetry(resourceID, response));
                        if ($.isFunction(callback) && response.Type !== messaging.MessageTypes.odataCallAttempted && response.Type !== messaging.MessageTypes.odataCallThrottled) {
                            callback(true, response);
                        }
                    }
                });
            });
        }

        // If the resource frame is not yet loaded, attempt to load it and progress the workflow;
        // otherwise send the request to the resource frame
        if (resourceInformation === undefined) {
            var url = appletInteraction.registeredResources.get(resourceID);

            if (url === undefined) {
                telemetry.errors.resourceNotRegistered.show(resources.appletInteraction.resourceNotRegisteredErrorFormat(resourceID));
                callback(false);
            } else if (appletInteraction.pendingResourceReadyCallbacks.containsKey(resourceID)) {
                appletInteraction.pendingResourceReadyCallbacks.get(resourceID).push(function resourceFrameAddedAdditionAction(successful, origin) {
                    if (successful) {
                        progressResourceRequest();
                    } else {
                        callback(false);
                    }
                });
            } else {
                var frameID = $format(appletInteraction._IdentifierFormats.ResourceFrame, resourceID);
                resourceInformation = new appletInteraction.ResourceInformation(resourceID);
                resourceInformation.Frame = appElements.addNewFrame(app.resourceDiv, frameID, url);

                var resourceReadyVerificationHandle = window.setTimeout(function verifyResourceIsReady() {
                    var readyCallbacks = appletInteraction.pendingResourceReadyCallbacks.get(resourceID);
                    if (readyCallbacks !== undefined) {
                        appletInteraction.unloadResource(resourceID);
                        $.each(readyCallbacks, function (index, readyCallback) {
                            readyCallback(false);
                        });
                    }
                }, HandleResourceRequestResourceLoadTimeout);

                appletInteraction.pendingResourceReadyCallbacks.add(resourceID, [function resourceFrameAdded(successful, origin) {
                    if (successful) {
                        window.clearTimeout(resourceReadyVerificationHandle);
                        resourceInformation.Origin = (origin || "").toUpperCase();
                        appletInteraction.resourceMap.add(resourceID, resourceInformation);
                        appletInteraction.sendMessageToResource(resourceID, new messaging.Requests.ConfigureResourceRequest(appState.State.Settings.HostName, appState.State.ServerConfig, appState.State.Features), false);
                        telemetry.events.resourceLoaded.log(resourceID, url);
                        progressResourceRequest();
                    } else {
                        callback(false);
                    }
                }]);
            }
        } else {
            progressResourceRequest();
        }
    }

    /**
     * Handles the appletMessaging.Requests.ResourceRequest event and sends the provided message to the specified resource.
     * If the request contains no request to send to the resource, the resource is just loaded.
     * @param event The request event.
     */
    export function handleResourceRequest(event: util.AddinMessageEvent<appletMessaging.Requests.ResourceRequest>): void {
        var request = event.data;
        var messageID = request.MessageID;
        var resourceID = request.Data.ResourceID;
        var resourceInformation: appletInteraction.ResourceInformation = appletInteraction.resourceMap.get(resourceID);

        // TODO: we should introduce a base request / response message used by resources to help us ensure things like if a response
        // was successful or not; that would allow us to send back a ResourceResponse with its status adjusted based on the resource's
        // response (TFS#3374089)
        function progressResourceRequest() {
            if (request.Data.ResourceRequest === undefined) {
                // If there is no resource request, just send a successful response to the applet
                var successfulResponse = new appletMessaging.Responses.ResourceResponse(request);
                successfulResponse.Status = appletMessaging.ResponseStatus.success;
                appletInteraction.sendMessageToApplet(event.data.AppletID, successfulResponse, false);
            } else {
                // Ensure the resource's response can be intercepted before sending the resource request to the resource
                appletInteraction.enhanceResourceRequest(request.Data.ResourceRequest, resourceID, messageID, function requestEnhanced() {
                    appletInteraction.sendMessageToResource(resourceID, request.Data.ResourceRequest, false);
                    appletInteraction.messageHandler.registerResponseCallback(messageID, function handleResourceResponse(resourceResponse: messaging.BaseResponse<messaging.BaseRequest>) {
                        if (!tryHandleODataRefreshHeadersMessage(resourceResponse, request.AppletID, resourceID, messageID)) {
                            window.setTimeout(() => logODataCorrelationTelemetry(resourceID, resourceResponse, request.AppletID));
                            if (resourceResponse.Type !== messaging.MessageTypes.odataCallAttempted && resourceResponse.Type !== messaging.MessageTypes.odataCallThrottled) {
                                var response = new appletMessaging.Responses.ResourceResponse(request, { ResourceResponse: resourceResponse });
                                response.Status = appletMessaging.ResponseStatus.success;
                                appletInteraction.sendMessageToApplet(request.AppletID, response, false);
                            }
                        }
                    });
                });
            }
        }

        function sendConfigurationTimedOutResponse() {
            var response = new appletMessaging.Responses.ResourceResponse(request);
            response.Status = appletMessaging.ResponseStatus.failure;
            response.FailureMessage = resources.appletInteraction.resourceConfigurationTimedOutErrorFormat(resourceID);
            var ResponseType = messaging.RequestResponseMap[request.Data.ResourceRequest === undefined ? "" : request.Data.ResourceRequest.Type];
            if (ResponseType !== undefined) {
                response.Data.ResourceResponse = new ResponseType();
                response.Data.ResourceResponse.Request = request.Data.ResourceRequest;
            }
            appletInteraction.sendMessageToApplet(request.AppletID, response, false);
        }

        // If a frame doesn't exist for the resource, create it, append it to the page HTML, and add it to the resource map
        if (resourceInformation === undefined) {
            var url = appletInteraction.registeredResources.get(resourceID);

            if (url === undefined) {
                var failedResponse = new appletMessaging.Responses.ResourceResponse(request);
                failedResponse.Status = appletMessaging.ResponseStatus.failure;
                failedResponse.FailureMessage = resources.appletInteraction.resourceNotRegisteredErrorFormat(resourceID);
                appletInteraction.sendMessageToApplet(event.data.AppletID, failedResponse, false);
            } else if (appletInteraction.pendingResourceReadyCallbacks.containsKey(resourceID)) {
                appletInteraction.pendingResourceReadyCallbacks.add(resourceID, function resourceFrameAddedAdditionalAction(success, origin) {
                    if (success) {
                        progressResourceRequest();
                    } else {
                        sendConfigurationTimedOutResponse();
                    }
                });
            } else {
                var frameID = $format(appletInteraction._IdentifierFormats.ResourceFrame, resourceID);
                resourceInformation = new appletInteraction.ResourceInformation(resourceID);
                resourceInformation.Frame = appElements.addNewFrame(app.resourceDiv, frameID, url);

                var resourceReadyVerificationHandle = window.setTimeout(function verifyResourceIsReady() {
                    var readyCallbacks = appletInteraction.pendingResourceReadyCallbacks.get(resourceID);
                    if (readyCallbacks !== undefined) {
                        appletInteraction.unloadResource(resourceID);
                        $.each(readyCallbacks, function (index, readyCallback) {
                            readyCallback(false);
                        });
                    }
                }, HandleResourceRequestResourceLoadTimeout);

                appletInteraction.pendingResourceReadyCallbacks.add(resourceID, [function resourceFrameAdded(success, origin) {
                    if (success) {
                        window.clearTimeout(resourceReadyVerificationHandle);
                        resourceInformation.Origin = (origin || "").toUpperCase();
                        appletInteraction.resourceMap.add(resourceID, resourceInformation);
                        appletInteraction.sendMessageToResource(resourceID, new messaging.Requests.ConfigureResourceRequest(appState.State.Settings.HostName, appState.State.ServerConfig, appState.State.Features), false);
                        telemetry.events.resourceLoaded.log(resourceID, url);
                        progressResourceRequest();
                    } else {
                        sendConfigurationTimedOutResponse();
                    }
                }]);
            }
        } else {
            progressResourceRequest();
        }
    }

    /**
     * Handles a resource ready request. Performs the registered pending resource ready callbacks.
     * @param event The message event received from the listener, containing the resource ready request.
     */
    export function _handleResourceReadyRequest(event: util.AddinMessageEvent<messaging.Requests.ResourceReadyRequest>): void {
        var resourceID = event.data.ResourceID;
        var resourceReadyCallbacks = appletInteraction.pendingResourceReadyCallbacks.get(resourceID);

        if (resourceReadyCallbacks !== undefined) {
            appletInteraction.pendingResourceReadyCallbacks.remove(resourceID);
            $.each(resourceReadyCallbacks, function (index, callback) {
                callback(true, event.origin);
            });
        }
    }

    /** Indicates if token acquisition is currently is progress. */
    export let enhanceResourceRequestProcessingTokenAcquisition = false;

    /** Contains pending token acquisition functions. */
    export let enhanceResourceRequestAcquireTokenQueue: (() => void)[] = [];

    /** The headers property that can optionally be contained in a data resource request. */
    export const EnhanceResourceRequestHeadersProperty = "Headers";

    /**
     * Enhances the provided resource request to contain additional appropriate information to ensure routing and other behaviors operate correctly.
     * @param request The request being sent to the resource corresponding to the provided resource ID.
     * @param resourceID The ID of the resource to send the provided request to.
     * @param messageID The ID of the request message containing the resource request.
     * @param callback The callback to invoke once the request is enhanced.
     */
    export function enhanceResourceRequest(request: messaging.BaseRequest, resourceID: string, messageID: string, callback: () => void): void {
        request.ResourceID = resourceID;
        request.MessageID = messageID;

        /** Acquires an OAuth token, resuming the processing of pending acquisitions once the acquisition is complete. */
        function acquireToken() {

            appletInteraction.enhanceResourceRequestProcessingTokenAcquisition = true;

            appState.State.AuthContext.acquireToken(appState.State.ServerConfig.AuthResourceId).then((accessToken: string) => {
                if (accessToken) {
                    (<any>request).Headers.Authorization = 'Bearer ' + accessToken;

                    callback();

                    if (appletInteraction.enhanceResourceRequestAcquireTokenQueue.length > 0) {
                        appletInteraction.enhanceResourceRequestAcquireTokenQueue.splice(0, 1)[0]();
                    } else {
                        appletInteraction.enhanceResourceRequestProcessingTokenAcquisition = false;
                    }
                } else {
                    unauthenticate("");
                }
            }, (error: AuthError) => {
                unauthenticate(authentication.authErrorToString(error));
            });
        }

        /**
         * Unauthenticates the session.
         * @param reason Reason for unauthenticating the session.
         */
        function unauthenticate(reason: string) {
            app.unauthenticate(function appUnauthenticated() {
                telemetry.errors.oAuthAuthenticationExpired.show(
                    resources.appletInteraction.authenticationError(),
                    authentication.extractAuthErrorCode(reason),
                    "",
                    {
                        Details: resources.appletInteraction.authenticationExpiredError(),
                        LinkOptions: {
                            Text: resources.dynamicsApp.signInButtonLabel(),
                            Action: () => {
                                authentication.getAuthenticator().requestSignIn();
                            }
                        }
                    }
                );
            });
        }

        if (resourceID === appletMessaging.WellKnownResources.Data && (appletInteraction.EnhanceResourceRequestHeadersProperty in request)) {
            appletInteraction._applyHeaders((<any>request).Headers);

            if (appState.State.AuthContext !== undefined) {

                // If token acquisition is currently is progress, add the acquisition to the queue; otherwise try acquiring the token
                if (appletInteraction.enhanceResourceRequestProcessingTokenAcquisition) {
                    appletInteraction.enhanceResourceRequestAcquireTokenQueue.push(acquireToken);
                } else {
                    acquireToken();
                }
            } else {
                callback();
            }
        } else {
            callback();
        }
    }

    /**
     * Applies common headers to the provided headers object.
     * @param headers The headers object of which to apply common headers.
     */
    export function _applyHeaders(headers: util.PropertyBag<string>): void {
        for (var header in appState.State.ServerConfig.Headers) {
            headers[header] = appState.State.ServerConfig.Headers[header].Value;
        }

        // Establish an activity ID header to track subsequent OData calls
        headers["ms-dyn-aid"] = appState.State.CorrelationID;
        headers["x-ms-client-session-id"] = appState.State.CorrelationID;

        // Establish a user agent header for the app
        headers["ms-dyn-useragent"] = "DynamicsOfficeApp/1";

        // Establish an accept-language header for the app
        var useOptionsLanguageForAcceptLanguage = appState.State.ServerConfig.Features?.indexOf("UseOptionsLanguageForAcceptLanguage") >= 0;
        if (!useOptionsLanguageForAcceptLanguage) {
            headers["Accept-Language"] = Globalize.culture().cultureName;
        } else {
            var appLanguage = appState.State.Language;
            var languages = copyLanguages(navigator.languages);
            languages.unshift(appLanguage);
            var acceptLanguage = buildAcceptLanguage(languages);
            headers["Accept-Language"] = acceptLanguage;
        }
    }

    function copyLanguages(languages: readonly string[]) {
        var copiedLanguages = [];
        languages.forEach((language) => {
            copiedLanguages.push(language)
        });
        return copiedLanguages;
    }

    function buildAcceptLanguage(languages: Array<string>) {
        var numberOfLanguagesMax = 100;
        if (languages.length > numberOfLanguagesMax) {
            languages = languages.slice(0, numberOfLanguagesMax)
        }
        var acceptLanguage = "";
        var weight = 1;
        for(var i = 0; i < languages.length; i++) {
            var formattedWeight = weight.toFixed(2);
            if (i != languages.length-1) {
                acceptLanguage += languages[i] + ";q=" + formattedWeight + ",";
            } else {
                acceptLanguage += languages[i] + ";q=" + formattedWeight;
            }
            // Weight have to remain within 1.0 and 0.0
            weight = weight - 0.01;
        }
        if (acceptLanguage === "") {
            return undefined;
        } else {
            return acceptLanguage.toLocaleLowerCase();
        }
    }

    /**
     * Sends the provided message to the applet corresponding to the provided applet ID.
     * @param appletID The ID of the applet to send the provided message to.
     * @param message The message to send to the applet.
     * @param cloneData Indicates if data should be cloned; defaults to true.
     */
    export function sendMessageToApplet(appletID: string, message: any, cloneData?: boolean): void {
        var appletInformation = appletInteraction.appletMap.get(appletID);

        if (appletInformation !== undefined) {

            // Send the message to the applet
            app.sendMessage(appletInformation.Frame, message, appState.State.ValidOriginSet[appletInformation.Origin], cloneData);

            // Determine if the message is a partial response
            var isPartialResponse = message.Partial === true || (message.Data && message.Data.ResourceResponse && message.Data.ResourceResponse.Partial === true);

            // If the message is a non-partial response that corresponds to an applet request, indicate that the applet request has completed
            if (!isPartialResponse && (message as appletMessaging.ResponseType).Request && (message as appletMessaging.ResponseType).Request.Type in appletMessaging.expectedResponseTypes) {
                appletInteraction.appletRequestCompleted();
            }
        }
    }

    /**
     * Sends the provided message to the resource corresponding to the provided resource ID.
     * @param resourceID The ID of the resource to send the provided message to.
     * @param message The message to send to the resource.
     * @param cloneData Indicates if data should be cloned; defaults to true.
     */
    export function sendMessageToResource(
        resourceID: string,
        message: messaging.BaseRequest | messaging.Responses.ODataCallRefreshHeadersMessage<messaging.BaseRequest>,
        cloneData?: boolean
    ): void {
        var resourceInformation = appletInteraction.resourceMap.get(resourceID);

        if (resourceInformation !== undefined) {
            var origin = appState.State.ValidOriginSet === undefined ? "*" : appState.State.ValidOriginSet[resourceInformation.Origin];
            app.sendMessage(resourceInformation.Frame, message, origin, cloneData);
        }
    }

    /** Contains functionality related to the UI for loading and unloading applets. */
    export namespace appletConfiguration {

        /** Contains class names related to the UI for loading and unloading applets. */
        export const ClassNames = {

            /** The class name for a checkbox's dependent elements div. */
            DependentApplets: "dependentApplets",

            /** The class name for an applet checkbox div. */
            AppletCheckbox: "appletCheckbox"
        };

        /** Contains attributes related to the UI for loading and unloading applets. */
        export const Attributes = {

            /** The applet checkbox attribute that indicates the corresponding applet's ID. */
            AppletID: "data-applet-id",

            /** The applet checkbox attribute containing the tooltip text. */
            Title: "title"
        };

        /** Contains element properties related to the UI for loading and unloading applets. */
        export const Properties = {

            /** The applet checkboxes' checked property. */
            Checked: "checked"
        };

        /** Contains selectors related to the UI for loading and unloading applets. */
        export const Selectors = {

            /** The selector used to find an applet checkbox's dependent applets div. */
            DependentApplets: $format(".{0}", appletInteraction.appletConfiguration.ClassNames.DependentApplets),

            /** The selector used to find an applet checkbox's immediate dependent applets div. */
            ImmediateDepenentApplets: $format("> .{0}", appletInteraction.appletConfiguration.ClassNames.DependentApplets),

            /** The selector used to find an applet checkbox. */
            AppletCheckbox: $format(".{0}", appletInteraction.appletConfiguration.ClassNames.AppletCheckbox),

            /** The selector used to find an applet checkbox's title span. */
            AppletCheckboxTitle: "> span",

            /** The selector used to find an applet checkbox's actual checkbox. */
            ImmediateCheckboxChildren: "> input[type='checkbox']",

            /** The selector used to find all of an applet checkbox's children checkboxes. */
            AllCheckboxChildren: "input[type='checkbox']"
        };

        interface AppletInfo {

            /** The ID of the applet. */
            ID: string;

            /** The ID of the applet's name label. */
            NameLabelID: string;

            /** The applet's name. */
            Name?: string;

            /** The ID of the applet's description label. */
            DescriptionLabelID: string;

            /** The applet's description. */
            Description?: string;

            /** The applet's retrieved registration. */
            AppletRegistration: RetrievedAppletRegistration;

            /** The ID of the applet on which the applet is dependent. */
            DependentOnID?: string;

            /** The applets that are dependent on the applet. */
            Dependents?: AppletInfo[];
        }

        /**
         * Generates applet configuration DOM (load / unload checkboxes) for the currently registered applets.
         * @param callback The callback to perform when all applets' DOMs are generated.
         */
        export function generateAppletConfigurationDOM(callback: () => void): void {

            // Iterate through the registered applets and create a structure that is more easily consumed, separating out
            // the root applets from the applets that have dependencies. The resulting structure will allow the traversal
            // of applets from root applets through all dependent applets
            var rootApplets = [];
            var appletInfoMap = new util.Map();
            $.each(appletInteraction.registeredApplets, function (index, applet) {

                let appletInfo: AppletInfo = {
                    ID: applet.AppletID,
                    NameLabelID: applet.Name,
                    DescriptionLabelID: applet.Description,
                    AppletRegistration: applet
                };

                if (applet.DependentOn) {
                    appletInfo.DependentOnID = applet.DependentOn;
                } else {
                    rootApplets.push(appletInfo);
                }

                appletInfoMap.add(appletInfo.ID, appletInfo);
            });

            // Iterate over the map of registered applets, applying dependent relationships where appropriate
            $.each(appletInfoMap.getKeys(), function (index, appletID) {
                var appletInfo = appletInfoMap.get(appletID);

                if (appletInfo.DependentOnID !== undefined) {
                    var dependentOnAppletInfo = appletInfoMap.get(appletInfo.DependentOnID);
                    if (dependentOnAppletInfo !== undefined) {
                        if (dependentOnAppletInfo.Dependents === undefined) {
                            dependentOnAppletInfo.Dependents = [];
                        }
                        dependentOnAppletInfo.Dependents.push(appletInfo);
                    }
                }
            });

            // Callback performed when an applet finishes generating its DOM, which will call the
            // provided callback when all applets' DOMs are generated
            var appletDomGenerationsPending = rootApplets.length;
            function appletConfigurationItemDomCompleted() {
                appletDomGenerationsPending--;
                if (appletDomGenerationsPending === 0) {
                    callback();
                }
            }

            // Iterate over the root applets and create their configuration DOM, which will recursively also
            // create the DOM for any dependent applets
            $.each(rootApplets, function (index, rootAppletInfo) {
                appletInteraction.appletConfiguration.generateAppletConfigurationItemDOM(
                    rootAppletInfo,
                    app.appletConfigurationDialog.configDialogContainer.configContent,
                    appletConfigurationItemDomCompleted);
            });
        }

        /**
         * Generates the applet configuration item DOM for the provided applet. This method will recurse through the
         * provided applet's dependent applets and create their DOM as well.
         * @param appletInfo The object container information about an applet.
         * @param parent The element for which to add the provided applet's DOM.
         * @param callback The callback to perform when the applet's DOM is generated.
         */
        export function generateAppletConfigurationItemDOM(appletInfo: AppletInfo, parent: JQuery, callback: () => void): void {
            function generateDOM() {

                // Create the applet checkbox
                var checkboxContainer = $("<div></div>", { "class": appletInteraction.appletConfiguration.ClassNames.AppletCheckbox })
                    .append($(`<input id="${appletInfo.ID}Checkbox" type="checkbox"></input>`))
                    .append($("<label></label>", { for: `${appletInfo.ID}Checkbox`, text: appletInfo.Name }))
                    .append($("<div></div>", { "class": appletInteraction.appletConfiguration.ClassNames.DependentApplets }))
                    .attr(appletInteraction.appletConfiguration.Attributes.AppletID, appletInfo.ID)
                    .appendTo(parent);

                // Set the tooltip for the checkbox if a description exists
                if (appletInfo.Description !== undefined && appletInfo.Description !== "") {
                    checkboxContainer.attr(appletInteraction.appletConfiguration.Attributes.Title, appletInfo.Description);
                }

                // Apply a clicked event handler for the checkbox that will appropriately check or uncheck parent and dependent
                // applet checkboxes
                var checkbox = checkboxContainer.find(appletInteraction.appletConfiguration.Selectors.ImmediateCheckboxChildren);
                checkbox.click(function appletConfigurationCheckboxClicked(event) {
                    if (checkbox.prop(appletInteraction.appletConfiguration.Properties.Checked)) {
                        appletInteraction.appletConfiguration.checkParents(checkbox);
                    } else {
                        appletInteraction.appletConfiguration.uncheckChildren(checkbox);
                    }
                });

                // Callback performed when a dependent applet finishes generating its DOM, which will call the
                // provided callback when all dependent applets' DOMs are generated
                var appletDomGenerationsPending = 0;
                function appletConfigurationItemDomCompleted() {
                    appletDomGenerationsPending--;
                    if (appletDomGenerationsPending === 0) {
                        callback();
                    }
                }

                // If the applet has dependent applets, recurse through those dependent applets and create their DOM as well,
                // adding them to the applet's dependent div
                if (appletInfo.Dependents !== undefined) {
                    appletDomGenerationsPending = appletInfo.Dependents.length;
                    var dependentsContainer = checkboxContainer.find(appletInteraction.appletConfiguration.Selectors.ImmediateDepenentApplets);
                    $.each(appletInfo.Dependents, function (index, dependentAppletInfo) {
                        appletInteraction.appletConfiguration.generateAppletConfigurationItemDOM(dependentAppletInfo, dependentsContainer, appletConfigurationItemDomCompleted);
                    });
                } else {
                    callback();
                }
            }

            // Determine if any labels need to be localized
            var nameLabelIdValid = appletInteraction.LabelIdValidationRegex.test(appletInfo.NameLabelID);
            var descriptionLabelIdValid = appletInteraction.LabelIdValidationRegex.test(appletInfo.DescriptionLabelID);

            // Before generating the DOM, send a request to localize the applet's Name and Description fields
            var request = new messaging.Requests.GetLabelsRequest();

            // Set the language to the app's language, or fall back to the current culture's name if the app's language isn't defined
            request.Language = appState.State.Language || Globalize.culture().cultureName;
            request.LabelIDs = [];

            // Only add to the request if the label IDs are valid
            if (nameLabelIdValid) {
                request.LabelIDs.push(appletInfo.NameLabelID);
            } else {
                appletInfo.Name = appletInfo.NameLabelID;
            }

            if (descriptionLabelIdValid) {
                request.LabelIDs.push(appletInfo.DescriptionLabelID);
            } else {
                appletInfo.Description = appletInfo.DescriptionLabelID;
            }

            // Send a request for the applet labels if localization is needed
            if (request.LabelIDs.length) {
                const labelIds = request.LabelIDs.slice(); // Create a copy to ensure array isn't modified
                appletInteraction.sendAppResourceRequest(
                    appletMessaging.WellKnownResources.Data,
                    request,
                    function appletLabelsRetrieved(success: boolean, response: messaging.Responses.GetLabelsResponse): void {

                        // If the response appears to be successful, verify the label results are correct
                        if (success) {

                            // Ensure the label results array exists and is the appropriate length
                            success = response.LabelResults && response.LabelResults.length === labelIds.length;

                            // If the label results array appears correct, verify it's entries are all non-null
                            if (success) {
                                for (const result of response.LabelResults) {
                                    if (!result) {
                                        success = false;
                                        break;
                                    }
                                }
                            }
                            if (!success) {
                                telemetry.events.appletLabelMissing.log(JSON.stringify(labelIds), JSON.stringify(response.LabelResults));
                            }
                        }

                        if (success) {

                            // The label results are in the order of Name -> Description
                            if (nameLabelIdValid) {
                                appletInfo.Name = response.LabelResults[0].Value || appletInfo.NameLabelID;
                                appletInfo.AppletRegistration.LocalizedName = appletInfo.Name;
                            }

                            if (descriptionLabelIdValid) {
                                appletInfo.Description = response.LabelResults[response.LabelResults.length - 1].Value || appletInfo.DescriptionLabelID;
                            }
                        } else {
                            // If the localization failed, log an error and set the Name to the label ID
                            notificationHandling.addErrorNotification(resources.appletInteraction.appletLocalizationErrorFormat(appletInfo.ID), "AppletLabelLocalizationFailed", { LogTelemetry: true });
                            appletInfo.Name = appletInfo.NameLabelID;
                        }

                        // Generate the DOM regardless of whether Name and Description have been localized
                        generateDOM();
                    });
            } else {
                // Generate the DOM using the label ID values if neither are valid label IDs
                generateDOM();
            }
        }

        interface AppletLoadInfo {

            /** The ID of the applet. */
            ID: string;

            /** The title / description of the applet. */
            Title: string;
        }

        /**
         * Applies the applet configuration dialog's current values to the app. This method will determine if any current applets have
         * been marked to be unloaded, as well as determining if any new applets have been indicated to be loaded. If any changes are
         * determined, the user will be prompted before loading / unloading applets; if no changes are detected, the dialog will be closed.
         */
        export function applyAppletConfiguration(): void {

            // The array of applets that have been requested to be loaded and unloaded
            let appletsRequestedToBeLoaded: AppletLoadInfo[] = [];
            let appletsRequestedToBeUnloaded: AppletLoadInfo[] = [];

            // Find all the applet checkbox elements
            var appletCheckboxes = app.appletConfigurationDialog.configDialogContainer.configContent
                .find(appletInteraction.appletConfiguration.Selectors.AppletCheckbox);

            // Iterate over all applet checkbox elements, and determine if any applets have been indicated to be loaded or unloaded
            $.each(appletCheckboxes, function (index, appletCheckbox) {

                // Find checkbox elements
                var $appletCheckbox = $(appletCheckbox);
                var checkbox = $appletCheckbox.find(appletInteraction.appletConfiguration.Selectors.ImmediateCheckboxChildren);
                var span = $appletCheckbox.find(appletInteraction.appletConfiguration.Selectors.AppletCheckboxTitle);

                // If the checkbox elements were found, determine if the corresponding applet has been indicated to be loaded or unloaded
                if (util.doesElementExist(checkbox) && util.doesElementExist(span)) {

                    // Retrieve information about the applet
                    var appletID = $appletCheckbox.attr(appletInteraction.appletConfiguration.Attributes.AppletID);
                    var appletTitle = span.text();
                    var appletAlreadyLoaded = appletInteraction.appletMap.containsKey(appletID);

                    // If the applet checkbox was checked and the applet wasn't already loaded, indicate it should be loaded; alternatively,
                    // if the applet checkbox wasn't checked but the applet is currently loaded, indicate it should be unloaded. All other
                    // cases indicate no change, so should no-op.
                    var checked = checkbox.prop(appletInteraction.appletConfiguration.Properties.Checked);
                    if (checked && !appletAlreadyLoaded) {
                        appletsRequestedToBeLoaded.push({
                            ID: appletID,
                            Title: appletTitle
                        });
                    } else if (!checked && appletAlreadyLoaded) {
                        appletsRequestedToBeUnloaded.push({
                            ID: appletID,
                            Title: appletTitle
                        });
                    }
                }
            });

            // Determine if any applets were indicated to be loaded and unloaded
            var appletsWillBeLoaded = appletsRequestedToBeLoaded.length > 0;
            var appletsWillBeUnloaded = appletsRequestedToBeUnloaded.length > 0;

            // If any applets were indicated to be loaded or unloaded, prompt the user confirming they want to load / unload the appropriate
            // applets; otherwise just close the dialog
            if (appletsWillBeLoaded || appletsWillBeUnloaded) {

                var message = resources.appletInteraction.userConfirmAppletsLoadedChangesMessage();

                // If applets will be unloaded, append a message indicating which applets will be unloaded
                if (appletsWillBeUnloaded) {
                    message = resources.appletInteraction.appletsToBeUnloadedMessageFormat(
                        appletsRequestedToBeUnloaded.map(appletInteraction.appletConfiguration._getAppletTitle)
                            .join(resources.appletInteraction.appletListSeparator()),
                        message);
                }

                // If applets will be loaded, append a message indicating which applets will be loaded
                if (appletsWillBeLoaded) {
                    message = resources.appletInteraction.appletsToBeLoadedMessageFormat(
                        appletsRequestedToBeLoaded.map(appletInteraction.appletConfiguration._getAppletTitle)
                            .join(resources.appletInteraction.appletListSeparator()),
                        message);
                }

                // Prompt the user to confirm they want to load / unload the indicated applets; upon them confirming, load / unload the
                // indicated applets
                notificationHandling.showConfirmationDialog(message, function appletConfigurationUserResponded(userResponse) {

                    // If the user confirmed they want to load / unload the indicated applets, load / unload the applets appropriately
                    if (userResponse) {

                        // Iterate over the registered applets, and place them into a map that can be more easily searched
                        var registeredAppletsMap = new util.Map();
                        $.each(appletInteraction.registeredApplets, function (index, applet) {
                            registeredAppletsMap.add(applet.AppletID, applet);
                        });

                        // Unload applets that were indicated to be unloaded
                        $.each(appletsRequestedToBeUnloaded, function (index, appletInfo) {
                            appletInteraction.unloadApplet(appletInfo.ID);
                        });

                        // Load applets that were indicated to be loaded
                        $.each(appletsRequestedToBeLoaded, function (index, appletInfo) {

                            var applet = registeredAppletsMap.get(appletInfo.ID);
                            if (applet !== undefined) {
                                appletInteraction.loadApplet(applet.AppletID, applet.Path, appState.State.Settings.Applets[applet.AppletID]);
                            }
                        });

                        // Close the configure applets dialog
                        dialogs.handler.closeActiveDialog(app.appletConfigurationDialog.dialogID);
                    }
                });
            } else {

                // Close the configure applets dialog
                dialogs.handler.closeActiveDialog(app.appletConfigurationDialog.dialogID);
            }
        }

        /**
         * Gets the title of the provided applet. This method is used as a callback provided to an array's map method to
         * convert an array of applet information objects into an array of applet titles.
         * @param currentValue The applet information object from which to retrieve the title.
         * @param index The index of the applet information object within the array.
         * @param array The array containing applet information objects.
         * @returns The applet information object's title.
         */
        export function _getAppletTitle(currentValue: AppletLoadInfo, index: number, array: AppletLoadInfo[]): string {
            return currentValue.Title;
        }

        /** Sets the applet check boxes based on the currently loaded applets. */
        export function resetConfigurationDialogValues(): void {

            // Find all applet checkboxes
            var appletCheckboxes = app.appletConfigurationDialog.configDialogContainer.configContent
                .find(appletInteraction.appletConfiguration.Selectors.AppletCheckbox);

            // Iterate over all the checkboxes, checking them if their corresponding applet is loaded, or unchecking them otherwise
            $.each(appletCheckboxes, function (index, appletCheckbox) {
                var $appletCheckbox = $(appletCheckbox);
                var checkbox = $appletCheckbox.find(appletInteraction.appletConfiguration.Selectors.ImmediateCheckboxChildren);
                if (util.doesElementExist(checkbox)) {
                    var appletID = $appletCheckbox.attr(appletInteraction.appletConfiguration.Attributes.AppletID);
                    checkbox.prop(appletInteraction.appletConfiguration.Properties.Checked, appletInteraction.appletMap.containsKey(appletID));
                }
            });
        }

        /**
         * Checks all the provided checkbox's parent checkboxes, resursively climbing the hierarchy of checkboxes until the root
         * checkbox is reached.
         * @param checkbox The checkbox for which to check parent checkboxes.
         */
        export function checkParents(checkbox: JQuery): void {
            var dependentAppletsContainer = checkbox.parent().parent(appletInteraction.appletConfiguration.Selectors.DependentApplets);
            if (util.doesElementExist(dependentAppletsContainer)) {
                var parentCheckbox = dependentAppletsContainer.parent(appletInteraction.appletConfiguration.Selectors.AppletCheckbox);
                if (util.doesElementExist(parentCheckbox)) {
                    var parentCheckboxControl = parentCheckbox.find(appletInteraction.appletConfiguration.Selectors.ImmediateCheckboxChildren);
                    if (util.doesElementExist(parentCheckboxControl)) {
                        parentCheckboxControl.prop(appletInteraction.appletConfiguration.Properties.Checked, true);
                        appletInteraction.appletConfiguration.checkParents(parentCheckboxControl);
                    }
                }
            }
        }

        /**
         * Unchecks all the provided checkbox's child checkboxes.
         * @param checkbox The checkbox for which to uncheck children checkboxes.
         */
        export function uncheckChildren(checkbox: JQuery): void {
            var checkboxParent = checkbox.parent();
            if (util.doesElementExist(checkboxParent)) {
                var childCheckboxes = checkboxParent.find(appletInteraction.appletConfiguration.Selectors.AllCheckboxChildren);
                if (util.doesElementExist(childCheckboxes)) {
                    childCheckboxes.prop(appletInteraction.appletConfiguration.Properties.Checked, false);
                }
            }
        }
    }
}
